import { db, auth } from './firebase'
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  onSnapshot
} from 'firebase/firestore'
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth'

import { useState, useEffect, useRef, useMemo } from "react";

/* ─────────────────────────────────────────────────────────────────────────────
   ELITE CRAVINGS BAKEHOUSE — CRM  v2
   Dual theme: Pure Black Dark  /  Pure White Light
   Gold (#C9A84C) accent carries through both modes
───────────────────────────────────────────────────────────────────────────── */

const GOLD     = "#C9A84C";
const GOLD_HOV = "#E0C064";
const GOLD_DIM = "rgba(201,168,76,0.12)";
const RED      = "#EF4444";
const GREEN_OK = "#10B981";
const AMBER    = "#F59E0B";
const BLUE_ACC = "#3B82F6";

// ── Theme palettes ────────────────────────────────────────────────────────────
const DARK = {
  bg:        "#060B14",
  surface:   "#0C1220",
  card:      "#111827",
  cardHov:   "#1A2332",
  border:    "rgba(201,168,76,0.10)",
  borderSub: "rgba(255,255,255,0.06)",
  text:      "#F0EDE6",
  textSub:   "#94918A",
  textDim:   "#4A4740",
  input:     "#0A1018",
  scrollBar: "#1E2A3A",
  sidebarBg: "#070D18",
  topBarBg:  "#080E1A",
  modalBg:   "#0F1724",
  aiStrip:   "#0A1018",
  rowAlt:    "#0D1525",
  emptyTxt:  "#1A2332",
  glow:      "rgba(201,168,76,0.06)",
  cardShadow:"0 2px 12px rgba(0,0,0,0.5), 0 0 0 1px rgba(201,168,76,0.06)",
  elevate:   "0 8px 32px rgba(0,0,0,0.6)",
};
const LIGHT = {
  bg:        "#F7F8FC",
  surface:   "#EFF1F6",
  card:      "#FFFFFF",
  cardHov:   "#FAFBFE",
  border:    "rgba(0,0,0,0.08)",
  borderSub: "rgba(0,0,0,0.05)",
  text:      "#0F172A",
  textSub:   "#64748B",
  textDim:   "#94A3B8",
  input:     "#F1F5F9",
  scrollBar: "#CBD5E1",
  sidebarBg: "#FFFFFF",
  topBarBg:  "#FFFFFF",
  modalBg:   "#FFFFFF",
  aiStrip:   "#F8FAFC",
  rowAlt:    "#F8FAFC",
  emptyTxt:  "#E2E8F0",
  glow:      "rgba(201,168,76,0.04)",
  cardShadow:"0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
  elevate:   "0 8px 30px rgba(0,0,0,0.08)",
};

const STAGES = [
  { id:"not_contacted",    label:"Not Contacted",     color:"#6B7280" },
  { id:"contacted",        label:"Contacted",         color:"#3B82F6" },
  { id:"sample_sent",      label:"Sample Sent",       color:"#C9A84C" },
  { id:"meeting_scheduled",label:"Meeting Scheduled", color:"#8B5CF6" },
  { id:"proposal_sent",    label:"Proposal Sent",     color:"#EC4899" },
  { id:"negotiating",      label:"Negotiating",       color:"#F97316" },
  { id:"won",              label:"Won 🏆",            color:"#10B981" },
  { id:"lost",             label:"Lost",              color:"#EF4444" },
];

const CATEGORIES = [
  { id:"restaurant",  label:"Restaurant Group",  color:"#DC6B3F", icon:"🍽" },
  { id:"hospital",    label:"Hospital",          color:"#E05C5C", icon:"🏥" },
  { id:"senior",      label:"Senior Living",     color:"#4A9E6E", icon:"🏡" },
  { id:"club",        label:"Country Club",      color:"#B8860B", icon:"⛳" },
  { id:"foodservice", label:"Food Service Co.",  color:"#3B6EA8", icon:"🚚" },
];

// Additional Lead Locator categories (user-configurable in Settings)
const EXTRA_CATS = [
  { id:"catering",   label:"Catering Companies",       icon:"🍱", color:"#E67E22", pipelineCat:"foodservice",
    osmQ:[`["shop"="catering"]`,`["catering"~".+"]`,`["amenity"="catering"]`],
    contact:"Director of Catering" },
  { id:"corporate",  label:"Corporate Offices",         icon:"🏢", color:"#5B6EAE", pipelineCat:"foodservice",
    osmQ:[`["office"~"^(company|corporation|financial|it|logistics|insurance|consulting)$"]`],
    contact:"Office Manager / Facilities" },
  { id:"school",     label:"Schools & Universities",    icon:"🎓", color:"#16A085", pipelineCat:"hospital",
    osmQ:[`["amenity"~"^(school|university|college|kindergarten)$"]`],
    contact:"Food Service Director" },
  { id:"government", label:"Federal / State Agencies",  icon:"🏛", color:"#8E44AD", pipelineCat:"foodservice",
    osmQ:[`["office"="government"]`,`["government"~".+"]["name"~".+"]`],
    contact:"Facilities Manager" },
  { id:"cityhall",   label:"City Halls & Courthouses",  icon:"⚖️", color:"#2980B9", pipelineCat:"foodservice",
    osmQ:[`["amenity"~"^(townhall|courthouse)$"]`],
    contact:"Administrative Manager" },
  { id:"emergency",  label:"Police & Fire Stations",    icon:"🚒", color:"#C0392B", pipelineCat:"foodservice",
    osmQ:[`["amenity"~"^(police|fire_station)$"]`],
    contact:"Station Commander" },
  { id:"library",    label:"Public Libraries",          icon:"📚", color:"#27AE60", pipelineCat:"foodservice",
    osmQ:[`["amenity"="library"]`],
    contact:"Library Director" },
  { id:"hotel",      label:"Hotels & Hospitality",      icon:"🏨", color:"#D35400", pipelineCat:"restaurant",
    osmQ:[`["tourism"~"^(hotel|motel|guest_house|hostel)$"]`],
    contact:"F&B Manager" },
  { id:"stadium",    label:"Stadiums & Event Venues",   icon:"🏟", color:"#7D3C98", pipelineCat:"restaurant",
    osmQ:[`["leisure"~"^(stadium|sports_centre|sports_hall)$"]`,`["amenity"="events_venue"]`],
    contact:"Catering / Events Director" },
];

const SEED = [
  { id:1,  company:"Kearns Restaurant Group",      contact:"F&B Director",        email:"info@krgdining.com",             phone:"239-337-3673", cat:"restaurant",  stage:"not_contacted",    priority:"high", address:"1404 Dean St, Fort Myers",                  notes:"Multi-concept group – Capone's, The Lodge, Firestone. One relationship = multiple locations.", sent:0, activity:null },
  { id:2,  company:"Rib City Group LLC",            contact:"VP Operations",       email:"ops@ribcity.com",                phone:"239-275-6700", cat:"restaurant",  stage:"contacted",        priority:"high", address:"6830 Shoppes at Plantation Dr, Fort Myers",  notes:"Multi-unit BBQ chain HQ. Slider buns & dinner rolls pitch.", sent:1, activity:"2025-02-18" },
  { id:3,  company:"Lee Memorial Hospital",         contact:"Food Service Dir.",   email:"fs@leehealth.org",               phone:"239-343-2000", cat:"hospital",    stage:"sample_sent",      priority:"high", address:"2776 Cleveland Ave, Fort Myers",             notes:"Lee Health System – one contact covers all 3 facilities. Sample delivered Feb 20.", sent:2, activity:"2025-02-20" },
  { id:4,  company:"Gulf Coast Medical Center",     contact:"Dietary Manager",     email:"dietary@leehealth.org",          phone:"239-343-1000", cat:"hospital",    stage:"not_contacted",    priority:"high", address:"13681 Doctors Way, Fort Myers",              notes:"Part of Lee Health system. Bundle with Lee Memorial.", sent:0, activity:null },
  { id:5,  company:"Grey Oaks Country Club",        contact:"Executive Chef",      email:"chef@greyoakscc.com",            phone:"239-262-5550", cat:"club",        stage:"meeting_scheduled",priority:"high", address:"2400 Grey Oaks Dr N, Naples",                notes:"Premier Naples club. Tasting scheduled March 1. Weddings & events focus.", sent:2, activity:"2025-02-19" },
  { id:6,  company:"Calusa Harbour Senior Living",  contact:"Dining Services Mgr", email:"dining@calusaharbour.com",       phone:"239-332-3333", cat:"senior",      stage:"contacted",        priority:"high", address:"2525 First St, Fort Myers",                  notes:"Upscale riverfront. Residents rave about food quality in reviews.", sent:1, activity:"2025-02-17" },
  { id:7,  company:"Canterfield of Fort Myers",     contact:"Executive Director",  email:"info@canterfieldoffortmyers.com",phone:"239-990-6100", cat:"senior",      stage:"proposal_sent",    priority:"high", address:"18301 Trifecta Ln, Fort Myers",              notes:"Perfect 5.0 rating. Premium positioning = ideal brand match. Proposal sent.", sent:3, activity:"2025-02-21" },
  { id:8,  company:"Naples Heritage Golf & CC",     contact:"F&B Director",        email:"fb@nhgcc.com",                   phone:"239-417-2555", cat:"club",        stage:"not_contacted",    priority:"med",  address:"8150 Heritage Club Way, Naples",             notes:"4.8-star club with active dining program. Closed Tuesdays.", sent:0, activity:null },
  { id:9,  company:"American House The Palms",      contact:"Dining Director",     email:"dining@americanhouse.com",       phone:"239-946-2114", cat:"senior",      stage:"won",              priority:"high", address:"2674 Winkler Ave, Fort Myers",               notes:"WON. Weekly delivery Tues/Fri. ~180 units/week.", sent:4, activity:"2025-02-21" },
  { id:10, company:"Physicians Regional Naples",    contact:"Food Service Dir.",   email:"fs@physiciansregional.com",      phone:"239-348-4000", cat:"hospital",    stage:"contacted",        priority:"med",  address:"6101 Pine Ridge Rd, Naples",                 notes:"Covers Pine Ridge + Collier Blvd. One system contact.", sent:1, activity:"2025-02-15" },
  { id:11, company:"Springwood Court AL",           contact:"Administrator",       email:"admin@springwoodcourtal.com",    phone:"239-278-0078", cat:"senior",      stage:"sample_sent",      priority:"med",  address:"12780 Kenwood Ln, Fort Myers",               notes:"4.9 stars. Awaiting feedback on sample box sent Feb 19.", sent:2, activity:"2025-02-19" },
  { id:12, company:"Lee County Jail (Aramark)",     contact:"Aramark Acct Mgr",    email:"corrections@aramark.com",        phone:"239-477-1500", cat:"foodservice", stage:"not_contacted",    priority:"med",  address:"2115 Dr MLK Jr Blvd, Fort Myers",            notes:"Contact Aramark Correctional – do NOT pitch facility directly.", sent:0, activity:null },
  { id:13, company:"Cheney Brothers Foodservice",   contact:"Regional Sales Mgr",  email:"sales@cheneybrothers.com",       phone:"407-000-0000", cat:"foodservice", stage:"not_contacted",    priority:"high", address:"Orlando, FL (SW FL territory)",              notes:"Major SW FL distributor. One approval = access to 100s of accounts.", sent:0, activity:null },
  { id:14, company:"The Summit of Fort Myers",      contact:"Dining Services Mgr", email:"dining@thesummitoffortmyers.com",phone:"239-977-1974", cat:"senior",      stage:"not_contacted",    priority:"med",  address:"1499 S Brandywine Cir, Fort Myers",          notes:"Active senior community. Events calendar = catering opportunity.", sent:0, activity:null },
];

const EMAIL_TEMPLATES = {
  restaurant:{subject:"Local Artisan Bakery Partnership — Elite Cravings Bakehouse",body:`Hi [Contact],

I'm reaching out from Elite Cravings Bakehouse, an artisan bakery in Estero serving restaurants across Southwest Florida.

We produce fresh croissants, artisan baguettes, brioche buns, and sourdough loaves daily using a commercial steam oven — baked same-morning and delivered to your kitchen before service. Always fresh, never frozen.

I'd love to drop off a complimentary sample box for your team this week. No commitment — just a taste.

Would Tuesday or Wednesday morning work for a quick drop-off?

Warm regards,
The Elite Cravings Bakehouse Team
Estero, FL  |  (239) 000-0000`},
  hospital:{subject:"Fresh Local Bakery Supply for Your Food Service Program",body:`Dear [Contact],

Elite Cravings Bakehouse is a licensed commercial artisan bakery in Estero, FL currently supplying healthcare facilities and senior living communities throughout Lee and Collier County.

We offer daily delivery of fresh-baked breads, rolls, croissants, and pastries — consistent quality, reliable schedule, local sourcing. Our multi-shift production supports institutional volume requirements at competitive pricing.

I'd welcome the chance to arrange a complimentary sample delivery and discuss how we fit your food service program.

Best regards,
The Elite Cravings Bakehouse Team
(239) 000-0000`},
  senior:{subject:"Fresh-Baked Artisan Pastries & Breads — Delivered Daily to Your Community",body:`Dear [Contact],

Your residents deserve more than a meal — they deserve a moment. The warm scent of a fresh croissant at breakfast, the crackle of sourdough at dinner.

Elite Cravings Bakehouse delivers fresh-baked croissants, pastries, sourdough, and specialty breads to senior living communities across Southwest Florida — baked fresh each morning, never frozen.

We'd love to bring a complimentary sample assortment for your dining team and residents. May I arrange a drop-off this week?

Warmly,
The Elite Cravings Bakehouse Team
(239) 000-0000`},
  club:{subject:"Artisan Breads & Pastries Worthy of Your Club — Elite Cravings Bakehouse",body:`Dear Chef [Contact],

Your members expect excellence in everything — the course, the service, the dining experience. The bread on your table should be no different.

Elite Cravings Bakehouse produces premium croissants, laminated pastries, artisan baguettes, brioche, and sourdough for Southwest Florida's finest clubs. Steam-baked, hand-crafted, delivered before service.

Would you have 20 minutes this week for a tasting?

With warm regards,
The Elite Cravings Bakehouse Team
(239) 000-0000`},
  foodservice:{subject:"SW Florida Commercial Bakery Seeking Distributor Partnership",body:`Dear [Contact],

Elite Cravings Bakehouse is a fully equipped commercial artisan bakery in Estero, FL — actively seeking distribution partnerships for the institutional accounts you serve in Southwest Florida.

We produce at scale daily, offer a full SKU range from dinner rolls through laminated pastries, and have private label / co-manufacturing available. FSMA-compliant with full vendor documentation ready.

I'd welcome a call to discuss distributor pricing and vendor qualification.

Best regards,
The Elite Cravings Bakehouse Team
(239) 000-0000`},
};

const DEFAULT_MANAGER_PERMS = {
  viewLeads:true, editLeads:true, deleteLeads:false,
  viewPipeline:true, editPipeline:true,
  viewEmailLog:true, sendEmails:true,
  viewCampaigns:true, manageCampaigns:false,
  useLocator:true,
  viewSettings:false, editSettings:false,
};
const PERM_GROUPS = [
  {label:"Leads",     perms:[{id:"viewLeads",l:"View"},{id:"editLeads",l:"Edit"},{id:"deleteLeads",l:"Delete"}]},
  {label:"Pipeline",  perms:[{id:"viewPipeline",l:"View"},{id:"editPipeline",l:"Edit"}]},
  {label:"Email",     perms:[{id:"viewEmailLog",l:"View Log"},{id:"sendEmails",l:"Send"}]},
  {label:"Campaigns", perms:[{id:"viewCampaigns",l:"View"},{id:"manageCampaigns",l:"Manage"}]},
  {label:"Tools",     perms:[{id:"useLocator",l:"Lead Locator"}]},
  {label:"Settings",  perms:[{id:"viewSettings",l:"View"},{id:"editSettings",l:"Edit"}]},
];

const getCat    = id => CATEGORIES.find(c=>c.id===id)||CATEGORIES[0];
const getAnyCat = id => CATEGORIES.find(c=>c.id===id)||EXTRA_CATS.find(c=>c.id===id)||CATEGORIES[0];
const getStage = id => STAGES.find(s=>s.id===id)||STAGES[0];
const initials = n  => n.split(" ").slice(0,2).map(w=>w[0]||"").join("").toUpperCase();
const daysAgo  = d  => { if(!d)return null; const n=Math.floor((Date.now()-new Date(d))/86400000); return n===0?"Today":n===1?"Yesterday":`${n}d ago`; };
const uid      = () => Date.now()+Math.random();

// ═════════════════════════════════════════════════════════════════════════════
// ROOT
// ═════════════════════════════════════════════════════════════════════════════
// ── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen(){
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const handleLogin=async(e)=>{
    e.preventDefault();setErr("");setLoading(true);
    try{await signInWithEmailAndPassword(auth,email,pass);}
    catch(ex){setErr(ex.code==="auth/invalid-credential"?"Invalid email or password.":ex.code==="auth/too-many-requests"?"Too many attempts. Try again later.":"Login failed. Please try again.");}
    setLoading(false);
  };
  return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#000",fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif"}}>
      <form onSubmit={handleLogin} style={{width:"92%",maxWidth:340,padding:"28px 24px",background:"#111",borderRadius:16,border:`1px solid #222`}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{color:GOLD,fontWeight:800,fontSize:22,letterSpacing:"-0.02em"}}>Elite Cravings</div>
          <div style={{color:"#888",fontSize:10,letterSpacing:"0.09em",textTransform:"uppercase",marginTop:4}}>Bakehouse · CRM</div>
        </div>
        <input value={email} onChange={e=>setEmail(e.target.value)} type="email" placeholder="Email" required
          style={{width:"100%",padding:"10px 14px",background:"#1A1A1A",border:"1px solid #333",borderRadius:8,color:"#E8E0D0",fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:10,boxSizing:"border-box"}}/>
        <input value={pass} onChange={e=>setPass(e.target.value)} type="password" placeholder="Password" required
          style={{width:"100%",padding:"10px 14px",background:"#1A1A1A",border:"1px solid #333",borderRadius:8,color:"#E8E0D0",fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:16,boxSizing:"border-box"}}/>
        {err&&<div style={{color:"#EF4444",fontSize:12,marginBottom:12,textAlign:"center"}}>{err}</div>}
        <button type="submit" disabled={loading}
          style={{width:"100%",padding:"11px 0",background:GOLD,color:"#000",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:loading?"wait":"pointer",fontFamily:"inherit",opacity:loading?0.7:1}}>
          {loading?"Signing in…":"Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const [user,setUser]=useState(undefined);
  useEffect(()=>{const unsub=onAuthStateChanged(auth,u=>setUser(u||null));return()=>unsub();},[]);

  if(user===undefined)return <div style={{height:"100vh",background:"#000"}}/>;
  if(!user)return <LoginScreen/>;
  return <AppContent user={user}/>;
}

function AppContent({user}) {
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [teamUsers, setTeamUsers] = useState([]);

  // Load user profile from Firestore (real-time)
  useEffect(()=>{
    const unsub = onSnapshot(doc(db,'users',user.uid), async snap=>{
      if(snap.exists()){
        setUserProfile({uid:user.uid,...snap.data()});
        setProfileLoading(false);
      } else {
        // Try to self-provision as admin (Firestore rules allow bootstrap create)
        try{
          const profile = {email:user.email,displayName:user.displayName||user.email.split("@")[0],role:"admin",permissions:{...DEFAULT_MANAGER_PERMS,deleteLeads:true,manageCampaigns:true,editSettings:true,viewSettings:true},createdAt:new Date().toISOString()};
          await setDoc(doc(db,'users',user.uid),profile);
        }catch{
          // Rules denied — not first user and not provisioned by admin
          setProfileLoading(false);
        }
      }
    }, ()=>setProfileLoading(false));
    return ()=>unsub();
  },[user.uid,user.email,user.displayName]);

  // Load all team users (for lead assignment + admin panel)
  useEffect(()=>{
    const unsub = onSnapshot(collection(db,'users'), snap=>{
      setTeamUsers(snap.docs.map(d=>({uid:d.id,...d.data()})));
    }, ()=>{});
    return ()=>unsub();
  },[]);

  const can = perm => {
    if(!userProfile) return false;
    if(userProfile.role==='admin') return true;
    return userProfile.permissions?.[perm]===true;
  };
  const isAdmin = userProfile?.role==='admin';

  const [darkMode,   setDarkMode]   = useState(false);
  const [leads,      setLeads]      = useState([]);
  const [page,       setPage]       = useState("dashboard");
  const [activeLead, setActiveLead] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [addModal,   setAddModal]   = useState(false);
  const [samplePackModal, setSamplePackModal] = useState(null);
  const [catFilter,  setCatFilter]  = useState("all");
  const [stgFilter,  setStgFilter]  = useState("all");
  const [q,          setQ]          = useState("");
  const [toast,      setToast]      = useState(null);
  const [emailLog,   setEmailLog]   = useState([]);
  const [emailSettings, setEmailSettings] = useState({apiEndpoint:"https://sendemail-e4raa4apka-uc.a.run.app",fromName:"Elite Cravings Bakehouse",fromEmail:"sales@elitecravings.com"});
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [isMobile,      setIsMobile]      = useState(()=>window.innerWidth<768);
  useEffect(()=>{
    const fn=()=>setIsMobile(window.innerWidth<768);
    window.addEventListener("resize",fn);
    return()=>window.removeEventListener("resize",fn);
  },[]);

  const T = darkMode ? DARK : LIGHT; // active theme

// Dark mode stays in localStorage (device preference)
useEffect(()=>{
  try{const s=localStorage.getItem("ec_dark");if(s!==null)setDarkMode(JSON.parse(s));}catch{}
},[]);
useEffect(()=>{
  try{localStorage.setItem("ec_dark",JSON.stringify(darkMode))}catch{}
},[darkMode]);

// Email settings — load from Firestore, fallback to localStorage
const settingsLoaded=useRef(false);
useEffect(()=>{
  (async()=>{
    try{
      const snap=await getDoc(doc(db,'settings','app'));
      if(snap.exists()){setEmailSettings(snap.data());}
      else{try{const s=localStorage.getItem("ec_email_settings");if(s)setEmailSettings(JSON.parse(s));}catch{}}
    }catch{
      try{const s=localStorage.getItem("ec_email_settings");if(s)setEmailSettings(JSON.parse(s));}catch{}
    }
    settingsLoaded.current=true;
  })();
},[]);
// Save settings to Firestore + localStorage on change
useEffect(()=>{
  if(!settingsLoaded.current)return;
  try{localStorage.setItem("ec_email_settings",JSON.stringify(emailSettings))}catch{}
  setDoc(doc(db,'settings','app'),emailSettings).catch(()=>{});
},[emailSettings]);

// Real-time leads from Firebase
useEffect(()=>{
  const unsub = onSnapshot(collection(db,'leads'), snapshot => {
    setLeads(snapshot.docs.map(d => ({ id: d.id, ...d.data() })))
  }, err => console.warn("Firestore leads listener error:", err.message))
  return () => unsub()
},[])

// Real-time email log from Firebase
useEffect(()=>{
  const unsub = onSnapshot(collection(db,'email_log'), snapshot => {
    const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
    setEmailLog(data.sort((a,b) => b.at > a.at ? 1 : -1))
  }, err => console.warn("Firestore email_log listener error:", err.message))
  return () => unsub()
},[])

  // inject scrollbar + base CSS
  useEffect(()=>{
    const fontId="ec-inter-font";
    if(!document.getElementById(fontId)){
      const link=document.createElement("link");
      link.id=fontId;link.rel="stylesheet";
      link.href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
      document.head.appendChild(link);
    }
    const id="ec-base-css";
    let el=document.getElementById(id);
    if(!el){el=document.createElement("style");el.id=id;document.head.appendChild(el);}
    el.textContent=`
      *{box-sizing:border-box;margin:0;padding:0}
      body{background:${T.bg};transition:background .3s ease;font-feature-settings:'cv02','cv03','cv04','cv11'}
      ::-webkit-scrollbar{width:6px;height:6px}
      ::-webkit-scrollbar-track{background:transparent}
      ::-webkit-scrollbar-thumb{background:${T.scrollBar};border-radius:10px}
      ::-webkit-scrollbar-thumb:hover{background:${GOLD}66}
      input:focus,select:focus,textarea:focus{border-color:${GOLD}88!important;box-shadow:0 0 0 3px ${GOLD}15,0 0 12px ${GOLD}08!important;outline:none!important}
      .lcard{transition:transform .2s ease,border-color .2s ease,box-shadow .25s ease!important}
      .lcard:hover{border-color:${GOLD}55!important;transform:translateY(-3px);box-shadow:${darkMode?"0 12px 32px rgba(0,0,0,0.6),0 0 0 1px rgba(201,168,76,0.12)":"0 12px 32px rgba(0,0,0,0.08),0 0 0 1px rgba(201,168,76,0.1)"}!important}
      .gbtn{background:linear-gradient(135deg,${GOLD} 0%,${GOLD_HOV} 100%)!important;transition:all .2s ease!important;position:relative;overflow:hidden}
      .gbtn:hover{box-shadow:0 4px 16px ${GOLD}44!important;transform:translateY(-1px)}
      .gbtn:active{transform:translateY(0)!important;box-shadow:0 2px 8px ${GOLD}33!important}
      .abtn{transition:all .2s ease!important}
      .abtn:hover{color:${GOLD}!important;border-color:${GOLD}55!important;background:${GOLD}08!important}
      .nbtn{transition:all .18s ease!important;position:relative}
      .nbtn:hover{background:${darkMode?"rgba(201,168,76,0.08)":"rgba(201,168,76,0.06)"}!important;color:${GOLD}!important}
      .trow{transition:background .15s ease!important}
      .trow:hover td{background:${darkMode?"rgba(201,168,76,0.04)":"rgba(201,168,76,0.03)"}!important}
      .stat-card{transition:transform .2s ease,box-shadow .2s ease!important}
      .stat-card:hover{transform:translateY(-2px);box-shadow:${T.elevate}!important}
      .nav-item{position:relative;overflow:hidden}
      .nav-item::after{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:${GOLD};transform:scaleY(0);transition:transform .2s ease;border-radius:0 3px 3px 0}
      .nav-item.active::after{transform:scaleY(1)}
      @keyframes spin{to{transform:rotate(360deg)}}
      @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
      @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      @keyframes slideIn{from{opacity:0;transform:translateY(-8px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
      @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
      @keyframes pulse{0%,100%{opacity:1}50%{opacity:.6}}
      select option{background:${T.surface};color:${T.text}}
    `;
  },[darkMode,T]);

  // Auth gate — after all hooks
  if(profileLoading) return <div style={{height:"100vh",background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{color:GOLD,fontSize:14}}>Loading...</div></div>;
  if(!userProfile) return(
    <div style={{height:"100vh",background:"#000",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div style={{textAlign:"center"}}>
        <div style={{color:GOLD,fontWeight:800,fontSize:22,marginBottom:8}}>Access Denied</div>
        <div style={{color:"#888",fontSize:13,marginBottom:20}}>Your account has not been provisioned. Contact your administrator.</div>
        <button onClick={()=>signOut(auth)} style={{padding:"9px 24px",background:GOLD,color:"#000",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Sign Out</button>
      </div>
    </div>
  );

  const notify = (msg,type="ok") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };
  const mutate = async (id, p) => {
  const today = new Date().toISOString().slice(0,10)
  await updateDoc(doc(db,'leads',id), { ...p, activity: today })
}
  const addLead = async (data) => {
  await addDoc(collection(db,'leads'), {
    ...data, stage:"not_contacted", sent:0, activity:null,
    assignedTo:data.assignedTo||"",assignedName:data.assignedName||""
  })
  notify(`${data.company} added!`)
}
  const removeLead = async (id) => {
  await deleteDoc(doc(db,'leads',id))
  if(activeLead?.id===id){ setActiveLead(null); setPage("pipeline") }
  notify("Lead removed.")
}
  const handleStageChange = (leadId, newStage, extraPatch={}) => {
    const patch = {stage:newStage, ...extraPatch};
    if(newStage==="sample_sent" && emailSettings.samplePacks?.length) {
      setSamplePackModal({leadId, patch});
    } else {
      mutate(leadId, patch);
    }
  };

  const logEmail = async (lead, subj, body) => {
  await addDoc(collection(db,'email_log'), {
    leadId: lead.id,
    company: lead.company,
    cat: lead.cat,
    subject: subj,
    body,
    at: new Date().toLocaleString()
  })
  await mutate(lead.id, {
    sent: (lead.sent||0)+1,
    stage: lead.stage==="not_contacted" ? "contacted" : lead.stage
  })
  notify(`Email logged for ${lead.company}`)
}

  const filtered = leads.filter(l=>{
    if(catFilter!=="all"&&l.cat!==catFilter)return false;
    if(stgFilter!=="all"&&l.stage!==stgFilter)return false;
    if(q&&!l.company.toLowerCase().includes(q.toLowerCase())&&!l.contact.toLowerCase().includes(q.toLowerCase()))return false;
    return true;
  });

  const stats={
    total:leads.length,
    active:leads.filter(l=>!["won","lost","not_contacted"].includes(l.stage)).length,
    won:leads.filter(l=>l.stage==="won").length,
    untouched:leads.filter(l=>l.stage==="not_contacted").length,
    highPri:leads.filter(l=>l.priority==="high"&&!["won","lost"].includes(l.stage)).length,
  };

  const shared = {T, darkMode, isMobile, can, isAdmin, userProfile, teamUsers};

  return (
    <div style={{display:"flex",height:"100vh",background:T.bg,color:T.text,fontFamily:"'Inter','Segoe UI',system-ui,-apple-system,sans-serif",overflow:"hidden",transition:"background .3s ease, color .3s ease",letterSpacing:"-0.01em"}}>
      {/* Mobile sidebar backdrop */}
      {isMobile&&sidebarOpen&&<div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:150,backdropFilter:"blur(2px)"}}/>}
      <Sidebar {...shared} page={page} setPage={p=>{setPage(p);setSidebarOpen(false);}} stats={stats}
        onAdd={()=>{setAddModal(true);setSidebarOpen(false);}} onToggle={()=>setDarkMode(d=>!d)}
        emailSettings={emailSettings} sidebarOpen={sidebarOpen} onClose={()=>setSidebarOpen(false)} user={user}
        can={can} isAdmin={isAdmin}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>
        <TopBar {...shared} darkMode={darkMode} page={page} q={q} setQ={setQ}
          catFilter={catFilter} setCatFilter={setCatFilter}
          stgFilter={stgFilter} setStgFilter={setStgFilter}
          activeLead={activeLead} onBack={()=>setPage("pipeline")}
          mottoText={emailSettings.mottoText||""}
          onMenuOpen={()=>setSidebarOpen(true)}/>
        <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
          {page==="dashboard" && <DashboardView {...shared} leads={leads} emailLog={emailLog} emailSettings={emailSettings} stats={stats} setPage={setPage}/>}
          {page==="pipeline" && can("viewPipeline") && <PipelineView {...shared} leads={filtered} emailSettings={emailSettings}
            onOpen={l=>{setActiveLead(l);setPage("detail");}}
            onEmail={l=>setEmailModal(l)} onStage={(id,s)=>handleStageChange(id,s)} onMutate={mutate}/>}
          {page==="list" && can("viewLeads") && <ListView {...shared} leads={filtered} emailSettings={emailSettings}
            onOpen={l=>{setActiveLead(l);setPage("detail");}} onEmail={l=>setEmailModal(l)} onMutate={mutate}/>}
          {page==="detail" && activeLead && <DetailView {...shared} emailSettings={emailSettings}
            lead={leads.find(l=>l.id===activeLead.id)||activeLead}
            emailLog={emailLog.filter(e=>e.leadId===activeLead.id)}
            onMutate={p=>p.stage?handleStageChange(activeLead.id,p.stage,p):mutate(activeLead.id,p)}
            onEmail={()=>setEmailModal(leads.find(l=>l.id===activeLead.id)||activeLead)}
            onDelete={()=>removeLead(activeLead.id)}/>}
          {page==="emails" && can("viewEmailLog") && <EmailLogView {...shared} emailLog={emailLog}/>}
          {page==="campaigns" && can("viewCampaigns") && <CampaignsView {...shared} leads={leads} emailSettings={emailSettings} notify={notify}/>}
          {page==="locator" && can("useLocator") && <LeadLocatorView {...shared} leads={leads} onAdd={addLead} emailSettings={emailSettings}/>}
          {page==="settings" && can("viewSettings") && <SettingsView {...shared} emailSettings={emailSettings} onSave={setEmailSettings} notify={notify}/>}
        </div>
      </div>

      {emailModal && <EmailModal {...shared} lead={emailModal} emailSettings={emailSettings}
        userEmail={user.email}
        onClose={()=>setEmailModal(null)}
        onSend={(s,b)=>{logEmail(emailModal,s,b);setEmailModal(null);}}
        notify={notify}/>}
      {addModal && can("editLeads") && <AddModal {...shared} onClose={()=>setAddModal(false)} onAdd={addLead} teamUsers={teamUsers}/>}
      {samplePackModal && <SamplePackModal {...shared} emailSettings={emailSettings} modal={samplePackModal}
        onConfirm={(leadId,patch)=>{mutate(leadId,patch);setSamplePackModal(null);}}
        onSkip={()=>{mutate(samplePackModal.leadId,samplePackModal.patch);setSamplePackModal(null);}}
        onCancel={()=>setSamplePackModal(null)}/>}
      {toast && <Toast T={T} darkMode={darkMode} msg={toast.msg} type={toast.type}/>}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({T,darkMode,isMobile,page,setPage,stats,onAdd,onToggle,emailSettings={},sidebarOpen,onClose,user,can,isAdmin}){
  const nav=[
    {id:"dashboard",icon:"📊",label:"Dashboard"},
    {id:"locator",icon:"📍",label:"Lead Locator",perm:"useLocator"},
    {id:"pipeline",icon:"⬡",label:"Pipeline",perm:"viewPipeline"},
    {id:"list",icon:"☰",label:"All Leads",perm:"viewLeads"},
    {id:"emails",icon:"✉",label:"Email Log",perm:"viewEmailLog"},
    {id:"campaigns",icon:"📣",label:"Campaigns",perm:"viewCampaigns"},
    {id:"settings",icon:"⚙",label:"Settings",perm:"viewSettings"},
  ].filter(n=>!n.perm||can(n.perm));
  return(
    <aside style={{
      width:240,background:T.sidebarBg,borderRight:`1px solid ${T.border}`,
      display:"flex",flexDirection:"column",padding:"0 0 16px",flexShrink:0,
      transition:"background .3s ease, transform .3s ease",
      ...(isMobile?{position:"fixed",top:0,left:0,height:"100vh",zIndex:200,
        transform:sidebarOpen?"translateX(0)":"translateX(-100%)",boxShadow:"8px 0 32px rgba(0,0,0,0.5)"}:{})
    }}>
      {/* Logo */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"20px 18px 18px",borderBottom:`1px solid ${T.border}`,background:darkMode?"rgba(201,168,76,0.02)":"transparent"}}>
        {emailSettings.brand1Icon
          ? <div style={{width:40,height:40,borderRadius:10,background:`linear-gradient(135deg,${GOLD}22,${GOLD}08)`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:`1px solid ${GOLD}22`}}>
              <span style={{fontSize:22,lineHeight:1}}>{emailSettings.brand1Icon}</span>
            </div>
          : <img src="/favicon.ico" alt="ECB" style={{width:40,height:40,borderRadius:10,flexShrink:0,objectFit:"contain"}}/>
        }
        <div style={{flex:1,minWidth:0}}>
          <div style={{color:emailSettings.brand1Color||GOLD,fontWeight:800,fontSize:14,letterSpacing:"-0.02em",fontFamily:emailSettings.brand1Font||"inherit",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {emailSettings.brand1Name||"Elite Cravings"}
          </div>
          <div style={{color:emailSettings.brand2Color||T.textSub,fontSize:9.5,letterSpacing:"0.09em",textTransform:"uppercase",marginTop:2,fontFamily:emailSettings.brand2Font||"inherit",fontWeight:500}}>
            {emailSettings.brand2Icon&&`${emailSettings.brand2Icon} `}{emailSettings.brand2Name||"Bakehouse · CRM"}
          </div>
        </div>
        {isMobile&&<button onClick={onClose} style={{background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:20,padding:"0 2px",lineHeight:1,flexShrink:0}}>✕</button>}
      </div>

      {/* Nav */}
      <nav style={{padding:"10px 10px 6px",flex:1}}>
        {nav.map(n=>{
          const active=page===n.id;
          return(
          <button key={n.id} className={`nbtn nav-item${active?" active":""}`}
            style={{display:"flex",alignItems:"center",gap:10,width:"100%",margin:"2px 0",
              padding:"9px 14px",borderRadius:8,
              background:active?(darkMode?"rgba(201,168,76,0.10)":"rgba(201,168,76,0.08)"):"none",
              border:"none",
              color:active?GOLD:T.textSub,cursor:"pointer",fontSize:13,
              fontFamily:"inherit",textAlign:"left",
              fontWeight:active?600:400}}
            onClick={()=>setPage(n.id)}>
            <span style={{fontSize:15,width:20,textAlign:"center",flexShrink:0,opacity:active?1:0.7}}>{n.icon}</span>
            <span>{n.label}</span>
            {active&&<span style={{width:6,height:6,borderRadius:"50%",background:GOLD,marginLeft:"auto",flexShrink:0,boxShadow:`0 0 8px ${GOLD}66`}}/>}
          </button>
        );})}
      </nav>

      {/* Stats */}
      <div style={{margin:"0 10px",padding:"14px 16px",background:darkMode?"rgba(201,168,76,0.03)":"rgba(201,168,76,0.03)",borderRadius:12,border:`1px solid ${T.border}`}}>
        <div style={{color:T.textDim,fontSize:9,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:12}}>Pipeline Overview</div>
        {[["Total",stats.total,T.text,""],["Active",stats.active,AMBER,""],["Won",stats.won,GREEN_OK,""],["Untouched",stats.untouched,RED,""],["High Pri",stats.highPri,GOLD,""]].map(([l,v,c])=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0"}}>
            <span style={{color:T.textSub,fontSize:11.5,fontWeight:500}}>{l}</span>
            <span style={{color:c,fontWeight:800,fontSize:14,fontVariantNumeric:"tabular-nums"}}>{v}</span>
          </div>
        ))}
      </div>

      {/* Theme toggle */}
      <button onClick={onToggle}
        style={{margin:"10px 10px 0",padding:"9px 14px",background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",
          border:`1px solid ${T.borderSub}`,borderRadius:9,cursor:"pointer",
          color:T.textSub,fontSize:12,fontFamily:"inherit",display:"flex",
          alignItems:"center",justifyContent:"center",gap:7,transition:"all .2s",fontWeight:500}}
        className="abtn">
        <span style={{fontSize:14}}>{darkMode ? "☀" : "🌙"}</span>
        {darkMode ? "Light Mode" : "Dark Mode"}
      </button>

      {/* Add lead */}
      {can("editLeads")&&<button className="gbtn" onClick={onAdd}
        style={{margin:"8px 10px 0",padding:"11px 16px",background:`linear-gradient(135deg,${GOLD},${GOLD_HOV})`,color:"#000",
          border:"none",borderRadius:9,cursor:"pointer",fontWeight:700,fontSize:13,
          fontFamily:"inherit",letterSpacing:"0.01em",
          display:"flex",alignItems:"center",justifyContent:"center",gap:7,
          boxShadow:`0 2px 12px ${GOLD}33`}}>
        <span style={{fontSize:16,lineHeight:1,fontWeight:400}}>+</span>Add Lead
      </button>}

      {/* User info + Sign out */}
      <div style={{margin:"8px 10px 0",padding:"10px 14px",background:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",borderRadius:9,border:`1px solid ${T.borderSub}`}}>
        <div style={{color:T.text,fontSize:12,fontWeight:600,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.displayName||user?.email?.split("@")[0]||"User"}</div>
        <div style={{color:T.textDim,fontSize:10,marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.email||""}</div>
        <button className="abtn" onClick={()=>signOut(auth)}
          style={{width:"100%",padding:"6px 0",background:"none",
            border:`1px solid ${T.borderSub}`,borderRadius:6,cursor:"pointer",
            color:T.textDim,fontSize:11,fontFamily:"inherit",
            transition:"all .2s",fontWeight:500}}>
          Sign Out
        </button>
      </div>
    </aside>
  );
}

// ── Top Bar ───────────────────────────────────────────────────────────────────
function TopBar({T,isMobile,page,q,setQ,catFilter,setCatFilter,stgFilter,setStgFilter,activeLead,onBack,mottoText,onMenuOpen,darkMode}){
  const titles={dashboard:"Dashboard",pipeline:"Sales Pipeline",list:"All Leads",detail:activeLead?.company||"Lead Detail",emails:"Email Log",locator:"Lead Locator",settings:"Admin Settings",campaigns:"Campaigns"};
  const inp={padding:"9px 14px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:8,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",transition:"border-color .2s, box-shadow .2s"};
  return(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:mottoText?"12px 20px 14px":"12px 20px",minHeight:mottoText?72:58,borderBottom:`1px solid ${T.border}`,background:T.topBarBg,flexShrink:0,flexWrap:"wrap",gap:10,transition:"background .3s ease"}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
        {isMobile&&<button onClick={onMenuOpen} style={{background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:22,padding:"2px 6px 0",lineHeight:1,marginTop:1}}>☰</button>}
        {page==="detail"&&<button className="abtn" style={{background:"none",border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.textSub,cursor:"pointer",fontSize:12,fontFamily:"inherit",padding:"4px 10px",marginTop:2}} onClick={onBack}>← Back</button>}
        <div>
          <h1 style={{color:T.text,fontSize:isMobile?16:19,fontWeight:800,letterSpacing:"-0.03em",lineHeight:1.3,margin:0}}>
            {titles[page]||""}
          </h1>
          {mottoText&&!isMobile&&<div style={{color:GOLD,fontSize:12,marginTop:5,fontWeight:500,display:"flex",alignItems:"center",gap:7,opacity:0.9}}>
            <span style={{width:4,height:4,borderRadius:"50%",background:GOLD,display:"inline-block",flexShrink:0,boxShadow:`0 0 6px ${GOLD}44`}}/>
            {mottoText}
          </div>}
          {page==="detail"&&activeLead&&<div style={{color:T.textSub,fontSize:12,marginTop:3}}>{getCat(activeLead.cat).icon} {getCat(activeLead.cat).label}</div>}
        </div>
      </div>
      {(page==="pipeline"||page==="list")&&(
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",flex:isMobile?1:undefined,minWidth:0}}>
          <div style={{position:"relative",flex:isMobile?1:undefined}}>
            <input style={{...inp,width:isMobile?"100%":200,paddingLeft:34,flexShrink:isMobile?1:0,minWidth:80}} placeholder="Search leads..." value={q} onChange={e=>setQ(e.target.value)}/>
            <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",fontSize:13,opacity:0.4,pointerEvents:"none"}}>🔍</span>
          </div>
          {!isMobile&&<>
            <select style={inp} value={catFilter} onChange={e=>setCatFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
            <select style={inp} value={stgFilter} onChange={e=>setStgFilter(e.target.value)}>
              <option value="all">All Stages</option>
              {STAGES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </>}
        </div>
      )}
    </div>
  );
}

// ── Map Modal ─────────────────────────────────────────────────────────────────
function MapModal({T,darkMode,leads,onClose,title="Lead Map",areaZip=""}){
  const withAddr=leads.filter(l=>(l.lat&&l.lng)||(l.address&&l.address.trim()));
  const locations=withAddr.map(l=>({
    company:l.company||l.name||"",
    address:l.address||"",
    stage:l.stage||"",
    priority:l.priority||"low",
    lat:l.lat||null,lng:l.lng||null,
  }));
  const html=`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>ECB CRM — Lead Map</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Inter','Segoe UI',sans-serif;background:#0D0D0D;color:#fff;display:flex;flex-direction:column;height:100vh}
#hdr{padding:10px 16px;background:#111;border-bottom:1px solid #222;display:flex;align-items:center;gap:10;flex-shrink:0}
#hdr h1{color:#C9A84C;font-size:14px;font-weight:700}#st{color:#888;font-size:12px;margin-left:auto}
#map{flex:1}.leaflet-popup-content-wrapper{border-radius:8px}.pc{font-weight:700;font-size:13px;color:#111;margin-bottom:3px}
.pa{font-size:11px;color:#555;margin-bottom:3px}.ps{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:#C9A84C}
.ph{color:#EF4444}.pm{color:#F59E0B}</style></head>
<body><div id="hdr"><span style="font-size:18px">🗺</span><h1>ECB CRM — Lead Map</h1><span id="st">Geocoding addresses…</span></div>
<div id="map"></div>
<script>
const locs=${JSON.stringify(locations)};
const areaZip="${areaZip}";
const map=L.map('map').setView([39.5,-98.35],4);
if(areaZip.length===5){geo(areaZip+", USA").then(c=>{if(c&&!markers.length)map.setView([c.lat,c.lon],11);});}
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{attribution:'© OpenStreetMap',maxZoom:19}).addTo(map);
const markers=[];
const pc={high:'#EF4444',med:'#F59E0B',low:'#6B7280'};
function icon(p){const c=pc[p]||'#C9A84C';const s=\`<svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34"><path d="M13 0C5.82 0 0 5.82 0 13c0 9.1 13 21 13 21S26 22.1 26 13C26 5.82 20.18 0 13 0z" fill="\${c}"/><circle cx="13" cy="13" r="5" fill="white"/></svg>\`;return L.divIcon({html:s,className:'',iconSize:[26,34],iconAnchor:[13,34],popupAnchor:[0,-34]});}
async function geo(addr){try{const r=await fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&q='+encodeURIComponent(addr),{headers:{'Accept-Language':'en'}});const d=await r.json();return d[0]?{lat:parseFloat(d[0].lat),lon:parseFloat(d[0].lon)}:null;}catch{return null;}}
function addMarker(l,lat,lon){const m=L.marker([lat,lon],{icon:icon(l.priority)}).addTo(map).bindPopup('<div class="pc">'+l.company+'</div><div class="pa">'+(l.address||'')+'</div><div class="ps '+(l.priority==='high'?'ph':l.priority==='med'?'pm':'')+'">'+(l.stage||'').replace(/_/g,' ')+(l.priority?' · '+l.priority+' pri':'')+'</div>');markers.push(m);}
async function run(){
  const st=document.getElementById('st');
  const direct=locs.filter(l=>l.lat&&l.lng);
  const needGeo=locs.filter(l=>!l.lat||!l.lng);
  direct.forEach(l=>{addMarker(l,l.lat,l.lng);});
  if(direct.length&&!needGeo.length){const g=L.featureGroup(markers);map.fitBounds(g.getBounds().pad(0.15));st.textContent=markers.length+' location'+(markers.length!==1?'s':'')+' mapped';return;}
  for(let i=0;i<needGeo.length;i++){
    const l=needGeo[i];st.textContent='Geocoding '+(i+1)+' of '+needGeo.length+': '+l.company;
    const c=await geo(l.address);
    if(c)addMarker(l,c.lat,c.lon);
    if(i<needGeo.length-1)await new Promise(r=>setTimeout(r,1100));
  }
  if(markers.length){const g=L.featureGroup(markers);map.fitBounds(g.getBounds().pad(0.15));st.textContent=markers.length+' location'+(markers.length!==1?'s':'')+' mapped';}
  else{st.textContent='No locations could be mapped';}
}
if(locs.length===0){document.getElementById('st').textContent='No locations to map';}else{run();}
</script></body></html>`;
  const blobUrl=useMemo(()=>{const b=new Blob([html],{type:'text/html'});return URL.createObjectURL(b);},[]);
  useEffect(()=>()=>URL.revokeObjectURL(blobUrl),[blobUrl]);
  return(
    <div style={{position:"fixed",inset:0,zIndex:1100,display:"flex",flexDirection:"column"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 18px",background:T.modalBg,borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
        <span style={{fontSize:18}}>🗺</span>
        <span style={{color:GOLD,fontWeight:700,fontSize:15}}>{title}</span>
        <span style={{color:T.textSub,fontSize:12}}>{withAddr.length} location{withAddr.length!==1?"s":""}</span>
        <button style={{marginLeft:"auto",background:"none",border:`1px solid ${T.border}`,borderRadius:6,color:T.textSub,cursor:"pointer",fontSize:13,padding:"4px 14px",fontFamily:"inherit"}} onClick={onClose}>✕ Close</button>
      </div>
      <iframe src={blobUrl} style={{flex:1,border:"none",width:"100%"}} title="Lead Map"/>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function DashboardView({T,darkMode,isMobile,leads,emailLog,emailSettings,stats,setPage}){
  const [campaigns,setCampaigns]=useState([]);
  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"campaigns"),snap=>{
      setCampaigns(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>b.createdAt>a.createdAt?1:-1));
    }, err=>console.warn("Firestore campaigns listener error:",err.message));
    return()=>unsub();
  },[]);

  // Stage distribution
  const stageCounts=STAGES.map(s=>({...s,count:leads.filter(l=>l.stage===s.id).length}));

  // Category breakdown
  const catCounts=CATEGORIES.map(c=>({...c,count:leads.filter(l=>l.cat===c.id).length}));

  // Email stats
  const totalEmailsSent=emailLog.length;
  const recentEmails=emailLog.slice(0,5);
  const emailsThisWeek=emailLog.filter(e=>{try{return(Date.now()-new Date(e.at))<7*864e5;}catch{return false;}}).length;

  // Campaign stats
  const campStats={
    total:campaigns.length,
    sent:campaigns.filter(c=>c.status==="sent").length,
    scheduled:campaigns.filter(c=>c.status==="scheduled").length,
    draft:campaigns.filter(c=>c.status==="draft").length,
    failed:campaigns.filter(c=>c.status==="failed").length,
    totalRecipients:campaigns.reduce((s,c)=>s+(c.recipientCount||0),0),
  };

  // Action items
  const staleLeads=leads.filter(l=>{
    if(["won","lost"].includes(l.stage))return false;
    if(!l.activity)return l.stage!=="not_contacted";
    return Math.floor((Date.now()-new Date(l.activity))/864e5)>=7;
  });
  const untouchedLeads=leads.filter(l=>l.stage==="not_contacted");
  const highPriActive=leads.filter(l=>l.priority==="high"&&!["won","lost"].includes(l.stage));

  // Conversion
  const closedDeals=leads.filter(l=>l.stage==="won").length;
  const totalEngaged=leads.filter(l=>l.stage!=="not_contacted").length;
  const conversionRate=totalEngaged>0?Math.round((closedDeals/totalEngaged)*100):0;

  // Mini map
  const mapHtml=useMemo(()=>{
    const withAddr=leads.filter(l=>(l.lat&&l.lng)||(l.address&&l.address.trim()));
    const locs=withAddr.map(l=>({company:l.company||"",address:l.address||"",stage:l.stage||"",priority:l.priority||"low",lat:l.lat||null,lng:l.lng||null}));
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
<style>*{margin:0;padding:0}body,html{height:100%;overflow:hidden}#map{height:100%;width:100%}
.leaflet-popup-content-wrapper{border-radius:8px}.pc{font-weight:700;font-size:12px;color:#111;margin-bottom:2px}
.pa{font-size:10px;color:#555}</style></head><body><div id="map"></div>
<script>
const locs=${JSON.stringify(locs)};
const areaZip="${emailSettings.areaZip||""}";
const map=L.map('map').setView([26.5,-81.8],10);
if(areaZip.length===5){geo(areaZip+", USA").then(c=>{if(c&&!markers.length)map.setView([c.lat,c.lon],11);});}
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{attribution:'',maxZoom:18}).addTo(map);
const markers=[];
const pc={high:'#EF4444',med:'#F59E0B',low:'#6B7280'};
function icon(p){const c=pc[p]||'#C9A84C';const s='<svg xmlns="http://www.w3.org/2000/svg" width="22" height="30" viewBox="0 0 26 34"><path d="M13 0C5.82 0 0 5.82 0 13c0 9.1 13 21 13 21S26 22.1 26 13C26 5.82 20.18 0 13 0z" fill="'+c+'"/><circle cx="13" cy="13" r="5" fill="white"/></svg>';return L.divIcon({html:s,className:'',iconSize:[22,30],iconAnchor:[11,30],popupAnchor:[0,-30]});}
async function geo(addr){try{const r=await fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&q='+encodeURIComponent(addr),{headers:{'Accept-Language':'en'}});const d=await r.json();return d[0]?{lat:parseFloat(d[0].lat),lon:parseFloat(d[0].lon)}:null;}catch{return null;}}
function addM(l,lat,lon){const m=L.marker([lat,lon],{icon:icon(l.priority)}).addTo(map).bindPopup('<div class="pc">'+l.company+'<\\/div><div class="pa">'+l.address+'<\\/div>');markers.push(m);}
async function run(){
const direct=locs.filter(l=>l.lat&&l.lng);const needGeo=locs.filter(l=>!l.lat||!l.lng);
direct.forEach(l=>{addM(l,l.lat,l.lng);});
for(let i=0;i<needGeo.length;i++){const l=needGeo[i];const c=await geo(l.address);if(c)addM(l,c.lat,c.lon);if(i<needGeo.length-1)await new Promise(r=>setTimeout(r,1100));}
if(markers.length){const g=L.featureGroup(markers);map.fitBounds(g.getBounds().pad(0.15));}
}
if(locs.length)run();
<\/script></body></html>`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[leads.length,emailSettings.areaZip]);
  const blobUrl=useMemo(()=>{const b=new Blob([mapHtml],{type:'text/html'});return URL.createObjectURL(b);},[mapHtml]);
  useEffect(()=>()=>URL.revokeObjectURL(blobUrl),[blobUrl]);

  const card={background:T.card,borderRadius:14,padding:isMobile?16:22,border:`1px solid ${T.border}`,boxShadow:T.cardShadow,transition:"transform .2s ease,box-shadow .2s ease"};
  const secTitle={color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14,display:"flex",alignItems:"center",gap:8};

  const wonLeads=leads.filter(l=>l.stage==="won");

  return(
    <div style={{flex:1,overflow:"auto",padding:isMobile?"12px 14px":"20px 24px"}}>

      {/* KPI Strip */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(5,1fr)",gap:14,marginBottom:20}}>
        {[["Total Leads",stats.total,T.text,"#64748B"],["Active",stats.active,AMBER,"#F59E0B"],["Won",stats.won,GREEN_OK,"#10B981"],["Untouched",stats.untouched,RED,"#EF4444"],["Conversion",conversionRate+"%",GOLD,GOLD]].map(([label,value,color,accent])=>(
          <div key={label} className="stat-card" style={{...card,textAlign:"center",padding:"20px 14px",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${accent},${accent}88)`,borderRadius:"14px 14px 0 0"}}/>
            <div style={{color,fontWeight:800,fontSize:isMobile?22:30,fontVariantNumeric:"tabular-nums",lineHeight:1.2}}>{value}</div>
            <div style={{color:T.textSub,fontSize:11,marginTop:6,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Widget Grid */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:16,animation:"fadeUp .4s ease"}}>

        {/* Pipeline Health */}
        <div style={{...card,cursor:"pointer"}} onClick={()=>setPage("pipeline")}>
          <div style={secTitle}>Pipeline Health</div>
          <div style={{display:"flex",height:10,borderRadius:10,overflow:"hidden",marginBottom:14,background:T.borderSub}}>
            {stageCounts.filter(s=>s.count>0).map(s=>(
              <div key={s.id} title={`${s.label}: ${s.count}`} style={{width:`${(s.count/Math.max(leads.length,1))*100}%`,background:s.color,transition:"width .3s"}}/>
            ))}
          </div>
          {stageCounts.map(s=>(
            <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0"}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:s.color,flexShrink:0}}/>
              <span style={{color:T.textSub,fontSize:12,flex:1}}>{s.label}</span>
              <span style={{color:s.count?s.color:T.textDim,fontWeight:700,fontSize:13,fontVariantNumeric:"tabular-nums"}}>{s.count}</span>
            </div>
          ))}
        </div>

        {/* Categories */}
        <div style={card}>
          <div style={secTitle}>Categories</div>
          {catCounts.map(c=>(
            <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"5px 0"}}>
              <span style={{fontSize:10,fontWeight:600,color:"#fff",padding:"2px 9px",borderRadius:20,background:c.color,letterSpacing:"0.04em",textTransform:"uppercase"}}>{c.icon} {c.label}</span>
              <span style={{marginLeft:"auto",color:c.count?T.text:T.textDim,fontWeight:700,fontSize:14,fontVariantNumeric:"tabular-nums"}}>{c.count}</span>
            </div>
          ))}
          <div style={{borderTop:`1px solid ${T.borderSub}`,marginTop:10,paddingTop:10,display:"flex",justifyContent:"space-between"}}>
            <span style={{color:T.textSub,fontSize:12}}>Total</span>
            <span style={{color:T.text,fontWeight:700,fontSize:14}}>{leads.length}</span>
          </div>
        </div>

        {/* Email Activity */}
        <div style={card}>
          <div style={secTitle}>Email Activity</div>
          <div style={{display:"flex",gap:16,marginBottom:14}}>
            <div style={{textAlign:"center",flex:1}}>
              <div style={{color:GOLD,fontWeight:800,fontSize:24}}>{totalEmailsSent}</div>
              <div style={{color:T.textSub,fontSize:11}}>Total Sent</div>
            </div>
            <div style={{textAlign:"center",flex:1}}>
              <div style={{color:AMBER,fontWeight:800,fontSize:24}}>{emailsThisWeek}</div>
              <div style={{color:T.textSub,fontSize:11}}>This Week</div>
            </div>
          </div>
          {recentEmails.length>0&&<>
            <div style={{color:T.textDim,fontSize:10,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Recent</div>
            {recentEmails.map((e,i)=>(
              <div key={e.id||i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${T.borderSub}`}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:T.text,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.company}</div>
                  <div style={{color:T.textDim,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.subject}</div>
                </div>
                <span style={{color:T.textDim,fontSize:10,flexShrink:0,marginLeft:8}}>{e.at?new Date(e.at).toLocaleDateString():""}</span>
              </div>
            ))}
          </>}
          <div style={{marginTop:10,textAlign:"center"}}>
            <button className="nbtn" onClick={()=>setPage("emails")} style={{background:"none",border:"none",color:GOLD,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>View All Emails →</button>
          </div>
        </div>

        {/* Campaign Performance */}
        <div style={card}>
          <div style={secTitle}>Campaigns</div>
          {campStats.total===0?(
            <div style={{textAlign:"center",padding:"16px 0"}}>
              <div style={{fontSize:32,marginBottom:8}}>📣</div>
              <div style={{color:T.textSub,fontSize:12}}>No campaigns yet</div>
            </div>
          ):(
            <>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                {[["Sent",campStats.sent,GREEN_OK],["Scheduled",campStats.scheduled,GOLD],["Drafts",campStats.draft,T.textDim],["Failed",campStats.failed,RED]].map(([label,val,color])=>(
                  <div key={label} style={{textAlign:"center",padding:"8px 0",background:darkMode?"rgba(255,255,255,0.025)":"rgba(0,0,0,0.025)",borderRadius:8}}>
                    <div style={{color,fontWeight:800,fontSize:20}}>{val}</div>
                    <div style={{color:T.textSub,fontSize:10}}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderTop:`1px solid ${T.borderSub}`}}>
                <span style={{color:T.textSub,fontSize:12}}>Total Recipients Reached</span>
                <span style={{color:GOLD,fontWeight:700,fontSize:14}}>{campStats.totalRecipients}</span>
              </div>
            </>
          )}
          <div style={{marginTop:10,textAlign:"center"}}>
            <button className="nbtn" onClick={()=>setPage("campaigns")} style={{background:"none",border:"none",color:GOLD,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>View Campaigns →</button>
          </div>
        </div>

        {/* Mini Lead Map */}
        <div style={{...card,padding:0,overflow:"hidden",gridColumn:isMobile?"1":"span 2"}}>
          <div style={{...secTitle,padding:"16px 20px 0 20px",marginBottom:8}}>Lead Map</div>
          <iframe src={blobUrl} style={{width:"100%",height:280,border:"none",borderRadius:"0 0 12px 12px"}} title="Dashboard Lead Map"/>
        </div>

        {/* Action Items */}
        <div style={{...card,gridColumn:isMobile?"1":"1 / -1"}}>
          <div style={secTitle}>Action Items</div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:16}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <span style={{width:8,height:8,borderRadius:"50%",background:RED}}/>
                <span style={{color:T.text,fontSize:12,fontWeight:600}}>Untouched ({untouchedLeads.length})</span>
              </div>
              {untouchedLeads.slice(0,4).map(l=>(
                <div key={l.id} style={{color:T.textSub,fontSize:12,padding:"3px 0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {getAnyCat(l.cat).icon} {l.company}
                </div>
              ))}
              {untouchedLeads.length>4&&<div style={{color:T.textDim,fontSize:11,marginTop:4}}>+ {untouchedLeads.length-4} more</div>}
            </div>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <span style={{width:8,height:8,borderRadius:"50%",background:AMBER}}/>
                <span style={{color:T.text,fontSize:12,fontWeight:600}}>Needs Follow-up ({staleLeads.length})</span>
              </div>
              {staleLeads.slice(0,4).map(l=>(
                <div key={l.id} style={{color:T.textSub,fontSize:12,padding:"3px 0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {getAnyCat(l.cat).icon} {l.company} <span style={{color:T.textDim}}>({daysAgo(l.activity)})</span>
                </div>
              ))}
              {staleLeads.length>4&&<div style={{color:T.textDim,fontSize:11,marginTop:4}}>+ {staleLeads.length-4} more</div>}
            </div>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <span style={{width:8,height:8,borderRadius:"50%",background:GOLD}}/>
                <span style={{color:T.text,fontSize:12,fontWeight:600}}>High Priority ({highPriActive.length})</span>
              </div>
              {highPriActive.slice(0,4).map(l=>(
                <div key={l.id} style={{color:T.textSub,fontSize:12,padding:"3px 0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {getStage(l.stage).label} — {l.company}
                </div>
              ))}
              {highPriActive.length>4&&<div style={{color:T.textDim,fontSize:11,marginTop:4}}>+ {highPriActive.length-4} more</div>}
            </div>
          </div>
        </div>

        {/* Recent Wins */}
        {wonLeads.length>0&&(
          <div style={{...card,gridColumn:isMobile?"1":"1 / -1"}}>
            <div style={secTitle}>Recent Wins</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {wonLeads.map(l=>(
                <div key={l.id} style={{display:"flex",alignItems:"center",gap:8,padding:"9px 16px",background:`${GREEN_OK}0A`,border:`1px solid ${GREEN_OK}22`,borderRadius:10}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:GREEN_OK,flexShrink:0,boxShadow:`0 0 8px ${GREEN_OK}44`}}/>
                  <span style={{color:T.text,fontSize:13,fontWeight:600}}>{l.company}</span>
                  <span style={{color:GREEN_OK,fontSize:11,fontWeight:700}}>Won</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Pipeline / Kanban ─────────────────────────────────────────────────────────
function PipelineView({T,darkMode,leads,onOpen,onEmail,onStage,onMutate,emailSettings,isMobile}){
  const [dragOver,setDragOver]=useState(null);
  const drop=(e,stageId)=>{
    e.preventDefault();
    const leadId=e.dataTransfer.getData('leadId');
    if(leadId)onStage(leadId,stageId);
    setDragOver(null);
  };
  return(
    <div style={isMobile?{display:"flex",flexDirection:"column",gap:12,flex:1,overflow:"auto",padding:"12px 14px"}:{display:"flex",overflowX:"auto",overflowY:"hidden",flex:1,padding:"16px 16px",gap:10}}>
      {STAGES.map(stage=>{
        const cols=leads.filter(l=>l.stage===stage.id);
        const over=dragOver===stage.id;
        return(
          <div key={stage.id}
            style={isMobile?{display:"flex",flexDirection:"column",borderRadius:12,background:over?`${stage.color}08`:"transparent",transition:"background .2s ease"}:{minWidth:190,maxWidth:210,flexShrink:0,display:"flex",flexDirection:"column",borderRadius:12,background:over?`${stage.color}08`:"transparent",transition:"background .2s ease"}}
            onDragOver={e=>{e.preventDefault();setDragOver(stage.id);}}
            onDragEnter={e=>{e.preventDefault();setDragOver(stage.id);}}
            onDragLeave={e=>{if(!e.currentTarget.contains(e.relatedTarget))setDragOver(null);}}
            onDrop={e=>drop(e,stage.id)}>
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:over?`${stage.color}15`:T.card,borderRadius:"12px 12px 0 0",marginBottom:6,border:`1px solid ${over?stage.color+"66":T.border}`,borderTop:`3px solid ${stage.color}`,transition:"all .2s ease",boxShadow:T.cardShadow}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:stage.color,flexShrink:0,display:"inline-block",boxShadow:`0 0 8px ${stage.color}44`}}/>
              <span style={{color:over?stage.color:T.textSub,fontSize:10,fontWeight:700,flex:1,letterSpacing:"0.05em",textTransform:"uppercase",transition:"color .2s"}}>{stage.label}</span>
              <span style={{background:cols.length?`${stage.color}18`:T.borderSub,color:cols.length?stage.color:T.textDim,fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:20,minWidth:22,textAlign:"center"}}>{cols.length}</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,overflowY:"auto",flex:1,paddingBottom:8,borderRadius:"0 0 12px 12px",outline:over?`2px dashed ${stage.color}44`:"2px dashed transparent",transition:"outline .2s",minHeight:48}}>
              {cols.map(l=><LeadCard key={l.id} T={T} darkMode={darkMode} lead={l} onOpen={onOpen} onEmail={onEmail} onStage={onStage} onMutate={onMutate} emailSettings={emailSettings}/>)}
              {!cols.length&&<div style={{color:over?stage.color:T.emptyTxt,fontSize:12,textAlign:"center",padding:"20px 0",fontStyle:"italic",transition:"color .2s"}}>{over?"Drop here":"Empty"}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LeadCard({T,darkMode,lead,onOpen,onEmail,onStage,onMutate,emailSettings}){
  const cat=getCat(lead.cat);
  const next=STAGES[STAGES.findIndex(s=>s.id===lead.stage)+1];
  const packBadge=lead.samplePackId?(emailSettings?.samplePacks||[]).find(p=>p.id===lead.samplePackId):null;
  const [dragging,setDragging]=useState(false);
  const priColor=lead.priority==="high"?RED:lead.priority==="med"?AMBER:"#6B7280";
  return(
    <div className="lcard"
      draggable
      onDragStart={e=>{e.dataTransfer.setData('leadId',String(lead.id));e.dataTransfer.effectAllowed='move';setDragging(true);}}
      onDragEnd={()=>setDragging(false)}
      onClick={()=>onOpen(lead)}
      style={{background:T.card,borderRadius:10,padding:"14px 14px",border:`1px solid ${T.border}`,borderLeft:`3px solid ${cat.color}`,cursor:dragging?"grabbing":"grab",opacity:dragging?0.4:1,transform:dragging?"scale(0.96)":"none",boxShadow:T.cardShadow,margin:"0 4px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <span style={{fontSize:9.5,fontWeight:700,color:"#fff",padding:"2px 9px",borderRadius:20,background:cat.color,letterSpacing:"0.04em",textTransform:"uppercase"}}>{cat.icon} {cat.label}</span>
        <span style={{width:8,height:8,borderRadius:"50%",background:priColor,flexShrink:0,display:"inline-block",boxShadow:`0 0 6px ${priColor}44`}} title={lead.priority+" priority"}/>
      </div>
      <div style={{color:T.text,fontWeight:700,fontSize:13,lineHeight:1.35,marginBottom:3}}>{lead.company}</div>
      <div style={{color:T.textSub,fontSize:11,marginBottom:3,fontWeight:500}}>{lead.contact}</div>
      {lead.assignedName&&<div style={{color:T.textDim,fontSize:10.5,marginBottom:4,display:"flex",alignItems:"center",gap:4}}>
        <span style={{opacity:0.7}}>👤</span> {lead.assignedName}
      </div>}
      {lead.activity&&<div style={{color:T.textDim,fontSize:10.5,marginBottom:4,display:"flex",alignItems:"center",gap:4}}>
        <span style={{opacity:0.7}}>🕐</span> {daysAgo(lead.activity)}
      </div>}
      {lead.stage==="sample_sent"&&(emailSettings?.samplePacks||[]).length>0&&(
        <div style={{marginBottom:6}} onClick={e=>e.stopPropagation()}>
          <select value={lead.samplePackId||""} onChange={e=>onMutate(lead.id,{samplePackId:e.target.value})}
            style={{width:"100%",padding:"5px 8px",borderRadius:6,fontSize:11,fontWeight:600,background:lead.samplePackId?`${GOLD}12`:T.input,border:`1px solid ${lead.samplePackId?GOLD+"55":T.borderSub}`,color:lead.samplePackId?GOLD:T.textSub,cursor:"pointer",fontFamily:"inherit",outline:"none"}}>
            <option value="">📦 Select pack…</option>
            {(emailSettings.samplePacks||[]).map(p=><option key={p.id} value={p.id}>📦 {p.name}</option>)}
          </select>
        </div>
      )}
      <div style={{display:"flex",alignItems:"center",gap:5,marginTop:9,paddingTop:9,borderTop:`1px solid ${T.borderSub}`,flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
        <button className="gbtn" style={{padding:"4px 10px",background:`linear-gradient(135deg,${GOLD},${GOLD_HOV})`,border:"none",borderRadius:5,color:"#000",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}
          onClick={e=>{e.stopPropagation();onEmail(lead);}}>✉ Email</button>
        {next&&!["won","lost"].includes(lead.stage)&&
          <button className="abtn" style={{padding:"4px 8px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:5,color:T.textSub,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}
            onClick={e=>{e.stopPropagation();onStage(lead.id,next.id);}}>→ {next.label.split(" ")[0]}</button>}
        <span style={{color:T.textDim,fontSize:10,marginLeft:"auto",fontWeight:600,fontVariantNumeric:"tabular-nums"}}>{lead.sent||0} sent</span>
      </div>
    </div>
  );
}

// ── List View ─────────────────────────────────────────────────────────────────
function ListView({T,darkMode,isMobile,leads,onOpen,onEmail,onMutate,emailSettings}){
  if(isMobile){
    return(
      <div style={{flex:1,overflow:"auto",padding:"10px 12px"}}>
        {leads.map(l=>{
          const cat=getCat(l.cat),stage=getStage(l.stage);
          const listPack=l.samplePackId?(emailSettings?.samplePacks||[]).find(p=>p.id===l.samplePackId):null;
          return(
            <div key={l.id} style={{background:T.card,borderRadius:10,border:`1px solid ${T.border}`,padding:"13px 14px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <button style={{background:"none",border:"none",color:T.text,cursor:"pointer",fontWeight:700,fontSize:14,fontFamily:"inherit",padding:0,textAlign:"left"}} onClick={()=>onOpen(l)}>{l.company}</button>
                <span style={{width:8,height:8,borderRadius:"50%",background:l.priority==="high"?RED:l.priority==="med"?AMBER:"#6B7280",flexShrink:0,marginTop:5,display:"inline-block"}}/>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8,alignItems:"center"}}>
                <span style={{fontSize:10,fontWeight:600,color:"#fff",padding:"2px 8px",borderRadius:20,background:cat.color}}>{cat.icon} {cat.label}</span>
                <select value={l.stage} onChange={e=>onMutate(l.id,{stage:e.target.value})}
                  style={{padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600,border:`1px solid ${stage.color}55`,background:stage.color+"18",color:stage.color,cursor:"pointer",fontFamily:"inherit",outline:"none"}}>
                  {STAGES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                {listPack&&<span title={`📦 ${listPack.name}`} style={{fontSize:11}}>📦</span>}
              </div>
              {l.contact&&<div style={{color:T.textSub,fontSize:12,marginBottom:4}}>{l.contact}</div>}
              {l.activity&&<div style={{color:T.textDim,fontSize:11,marginBottom:8}}>🕐 {daysAgo(l.activity)}</div>}
              <div style={{display:"flex",gap:8}}>
                <button className="gbtn" style={{flex:1,padding:"6px",background:GOLD,border:"none",borderRadius:6,color:"#000",cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}} onClick={()=>onEmail(l)}>✉ Email</button>
                <button className="abtn" style={{flex:1,padding:"6px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.textSub,cursor:"pointer",fontSize:12,fontFamily:"inherit"}} onClick={()=>onOpen(l)}>View →</button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return(
    <div style={{flex:1,overflow:"auto",padding:"10px 24px 24px"}}>
      <div style={{display:"flex",alignItems:"center",gap:16,padding:"10px 10px 16px",flexWrap:"wrap"}}>
        <span style={{color:T.textDim,fontSize:9,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",flexShrink:0}}>Categories</span>
        {CATEGORIES.map(c=>(
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 10px",borderRadius:20,background:`${c.color}0A`}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:c.color,flexShrink:0,display:"inline-block"}}/>
            <span style={{color:T.textSub,fontSize:11,fontWeight:500}}>{c.icon} {c.label}</span>
          </div>
        ))}
      </div>
      <div style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,overflow:"hidden",boxShadow:T.cardShadow}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead>
          <tr style={{background:darkMode?"rgba(201,168,76,0.03)":"rgba(0,0,0,0.02)"}}>{["Company","Category","Stage","Contact","Priority","Activity","Emails",""].map(h=>(
            <th key={h} style={{color:T.textDim,fontSize:10,fontWeight:700,textAlign:"left",padding:"13px 12px 11px",borderBottom:`1px solid ${T.border}`,letterSpacing:"0.08em",textTransform:"uppercase"}}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {leads.map((l,i)=>{
            const cat=getCat(l.cat),stage=getStage(l.stage);
            const listPack=l.samplePackId?(emailSettings?.samplePacks||[]).find(p=>p.id===l.samplePackId):null;
            return(
              <tr key={l.id} className="trow" style={{background:i%2===0?T.card:T.rowAlt,transition:"background .15s"}}>
                <td style={{padding:"11px 10px",borderBottom:`1px solid ${T.borderSub}`,fontSize:13}}>
                  <button style={{background:"none",border:"none",color:T.text,cursor:"pointer",fontWeight:600,fontSize:13,fontFamily:"inherit",padding:0,textAlign:"left"}} onClick={()=>onOpen(l)}>{l.company}</button>
                </td>
                <td style={{padding:"11px 10px",borderBottom:`1px solid ${T.borderSub}`,textAlign:"center"}}>
                  <span style={{width:11,height:11,borderRadius:"50%",background:cat.color,display:"inline-block"}} title={`${cat.icon} ${cat.label}`}/>
                </td>
                <td style={{padding:"8px 10px",borderBottom:`1px solid ${T.borderSub}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <select value={l.stage} onChange={e=>onMutate(l.id,{stage:e.target.value})} onClick={e=>e.stopPropagation()}
                      style={{padding:"3px 8px",borderRadius:12,fontSize:11,fontWeight:600,border:`1px solid ${stage.color}55`,background:stage.color+"18",color:stage.color,cursor:"pointer",fontFamily:"inherit",outline:"none",maxWidth:140}}>
                      {STAGES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    {listPack&&<span style={{fontSize:10}} title={`📦 ${listPack.name}`}>📦</span>}
                  </div>
                </td>
                <td style={{padding:"11px 10px",borderBottom:`1px solid ${T.borderSub}`,fontSize:13,color:T.textSub}}>{l.contact}</td>
                <td style={{padding:"8px 10px",borderBottom:`1px solid ${T.borderSub}`}}>
                  <select value={l.priority} onChange={e=>onMutate(l.id,{priority:e.target.value})} onClick={e=>e.stopPropagation()}
                    style={{fontWeight:700,fontSize:12,color:l.priority==="high"?RED:l.priority==="med"?AMBER:"#6B7280",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:5,padding:"3px 7px",cursor:"pointer",fontFamily:"inherit",outline:"none"}}>
                    <option value="high">High</option><option value="med">Med</option><option value="low">Low</option>
                  </select>
                </td>
                <td style={{padding:"11px 10px",borderBottom:`1px solid ${T.borderSub}`,fontSize:12,color:T.textSub}}>{daysAgo(l.activity)||"—"}</td>
                <td style={{padding:"11px 10px",borderBottom:`1px solid ${T.borderSub}`,fontSize:13,color:T.textSub}}>{l.sent||0}</td>
                <td style={{padding:"11px 10px",borderBottom:`1px solid ${T.borderSub}`}}>
                  <button className="gbtn" style={{padding:"4px 10px",background:GOLD,border:"none",borderRadius:4,color:"#000",cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600,marginRight:4,transition:"background .15s"}} onClick={()=>onEmail(l)}>✉</button>
                  <button className="abtn" style={{padding:"4px 10px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:4,color:T.textSub,cursor:"pointer",fontSize:12,transition:"all .15s"}} onClick={()=>onOpen(l)}>→</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── Detail View ───────────────────────────────────────────────────────────────
function DetailView({T,darkMode,isMobile,lead,emailLog,onMutate,onEmail,onDelete,emailSettings,can,teamUsers=[]}){
  const cat=getCat(lead.cat),stage=getStage(lead.stage),si=STAGES.findIndex(s=>s.id===lead.stage);
  const cardStyle={background:T.card,borderRadius:14,padding:22,border:`1px solid ${T.border}`,marginBottom:0,boxShadow:T.cardShadow};
  const inp={width:"100%",padding:"9px 12px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:8,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",marginTop:4,transition:"border-color .2s"};
  const samplePacks=emailSettings?.samplePacks||[];
  const selectedPack=samplePacks.find(p=>p.id===lead.samplePackId);
  return(
    <div style={{display:"flex",flexDirection:isMobile?"column":"row",gap:18,padding:isMobile?"12px 14px":"18px 22px",flex:1,overflow:"auto"}}>
      {/* Left */}
      <div style={{width:isMobile?"100%":285,flexShrink:0,display:"flex",flexDirection:"column",gap:14}}>
        <div style={cardStyle}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
            <div style={{width:64,height:64,borderRadius:16,background:`linear-gradient(135deg,${cat.color},${cat.color}cc)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:19,boxShadow:`0 6px 20px ${cat.color}44`,letterSpacing:"-0.02em"}}>{initials(lead.company)}</div>
          </div>
          <div style={{color:T.text,fontWeight:700,fontSize:16,textAlign:"center"}}>{lead.company}</div>
          <div style={{color:T.textSub,fontSize:13,textAlign:"center",marginTop:3}}>{lead.contact}</div>
          <div style={{textAlign:"center",marginTop:8}}><span style={{fontSize:10,fontWeight:600,color:"#fff",padding:"2px 9px",borderRadius:20,background:cat.color,letterSpacing:"0.04em",textTransform:"uppercase"}}>{cat.icon} {cat.label}</span></div>
          <div style={{marginTop:16,display:"flex",flexDirection:"column",gap:11}}>
            {[["📧","Email",lead.email],["📞","Phone",lead.phone],["📍","Location",lead.address]].map(([ic,lb,val])=>(
              <div key={lb} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{fontSize:14,width:20,textAlign:"center",flexShrink:0,marginTop:1}}>{ic}</span>
                <div><div style={{color:T.textDim,fontSize:10,textTransform:"uppercase",letterSpacing:"0.07em"}}>{lb}</div><div style={{color:T.textSub,fontSize:12,marginTop:1,wordBreak:"break-all"}}>{val||"—"}</div></div>
              </div>
            ))}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>Notes</div>
          <textarea style={{...inp,minHeight:88,resize:"vertical",lineHeight:1.6,fontSize:12,padding:"9px 11px"}}
            value={lead.notes||""} onChange={e=>onMutate({notes:e.target.value})} placeholder="Add notes about this account…"/>
        </div>

        {can("deleteLeads")&&<button style={{background:"none",border:`1px solid ${RED}44`,borderRadius:6,color:RED,cursor:"pointer",padding:"8px 14px",fontSize:12,fontFamily:"inherit",alignSelf:"flex-start"}} onClick={onDelete}>
          🗑 Remove Lead
        </button>}
      </div>

      {/* Right */}
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:14,overflow:"auto"}}>
        <div style={cardStyle}>
          <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>Pipeline Status</div>
          <div style={{display:"flex",gap:3,height:8,marginBottom:8}}>
            {STAGES.filter(s=>s.id!=="lost").map((s,i)=>(
              <button key={s.id} title={s.label}
                style={{flex:1,height:"100%",borderRadius:3,border:"none",cursor:"pointer",
                  background:i<=si?s.color:T.borderSub,
                  transform:s.id===lead.stage?"scaleY(1.7)":"none",
                  opacity:s.id===lead.stage?1:0.6,transition:"all .22s"}}
                onClick={()=>onMutate({stage:s.id})}/>
            ))}
          </div>
          <div style={{textAlign:"center",color:stage.color,fontWeight:700,fontSize:13,marginBottom:14}}>{stage.label}</div>
          <div style={{display:"flex",gap:12}}>
            {[["Stage",lead.stage,s=>onMutate({stage:s}),STAGES.map(s=>({v:s.id,l:s.label}))],
              ["Priority",lead.priority,v=>onMutate({priority:v}),[{v:"high",l:"High"},{v:"med",l:"Medium"},{v:"low",l:"Low"}]]].map(([lb,val,fn,opts])=>(
              <div key={lb} style={{flex:1}}>
                <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:4}}>{lb}</label>
                <select style={{...inp,padding:"7px 10px"}} value={val} onChange={e=>fn(e.target.value)}>
                  {opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            ))}
          </div>
          {teamUsers.length>0&&<div style={{marginTop:12}}>
            <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:4}}>Assigned To</label>
            <select style={{...inp,padding:"7px 10px"}} value={lead.assignedTo||""} onChange={e=>{const u=teamUsers.find(u=>u.uid===e.target.value);onMutate({assignedTo:e.target.value,assignedName:u?u.displayName||u.email:""});}}>
              <option value="">Unassigned</option>
              {teamUsers.map(u=><option key={u.uid} value={u.uid}>{u.displayName||u.email}</option>)}
            </select>
          </div>}
        </div>

        {lead.stage==="sample_sent"&&(
          <div style={cardStyle}>
            <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>📦 Sample Pack Sent</div>
            {samplePacks.length===0
              ? <div style={{color:T.textSub,fontSize:12,lineHeight:1.6}}>Define sample packs in <strong style={{color:T.text}}>Settings → 📦 Sample Packs</strong> to track what was delivered.</div>
              : <>
                  <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:4}}>Pack Recorded</label>
                  <select style={{...inp,marginTop:0}} value={lead.samplePackId||""} onChange={e=>onMutate({samplePackId:e.target.value})}>
                    <option value="">— Not recorded —</option>
                    {samplePacks.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {selectedPack&&selectedPack.items.length>0&&(
                    <ul style={{marginTop:10,paddingLeft:16,color:T.textSub,fontSize:12,lineHeight:1.8,margin:"10px 0 0"}}>
                      {selectedPack.items.map((item,i)=><li key={i}>{item}</li>)}
                    </ul>
                  )}
                  {!selectedPack&&<div style={{color:T.textDim,fontSize:11,marginTop:6}}>Select a pack above to log what was delivered.</div>}
                  {lead.activity&&<div style={{color:T.textDim,fontSize:11,marginTop:8}}>Date recorded: {lead.activity}</div>}
                </>
            }
          </div>
        )}

        <div style={cardStyle}>
          <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:12}}>Quick Actions</div>
          <div style={{display:"flex",gap:10,marginBottom:12}}>
            <button className="gbtn" style={{flex:1,padding:"14px 8px",background:`linear-gradient(135deg,${GOLD},${GOLD_HOV})`,border:"none",borderRadius:10,color:"#000",cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:5,fontWeight:700,fontSize:12,boxShadow:`0 2px 12px ${GOLD}33`}} onClick={onEmail}>
              <span style={{fontSize:22}}>✉</span>Compose Email
            </button>
            {si<STAGES.length-2&&(
              <button className="abtn" style={{flex:1,padding:"14px 8px",background:"none",border:`1px solid ${T.border}`,borderRadius:10,color:T.textSub,cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:5,fontSize:12}}
                onClick={()=>onMutate({stage:STAGES[si+1].id})}>
                <span style={{fontSize:22}}>→</span>Advance Stage
              </button>
            )}
          </div>
          <div style={{paddingTop:10,borderTop:`1px solid ${T.borderSub}`,textAlign:"center",fontSize:13}}>
            <span style={{color:GOLD,fontWeight:700}}>{lead.sent||0}</span>
            <span style={{color:T.textDim,marginLeft:6,fontSize:12}}>emails logged</span>
          </div>
        </div>

        {emailLog.length>0&&(
          <div style={cardStyle}>
            <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:12}}>Email History ({emailLog.length})</div>
            {emailLog.map(e=>(
              <div key={e.id} style={{padding:"10px 12px",background:T.input,borderRadius:6,marginBottom:8,border:`1px solid ${T.borderSub}`}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <div style={{color:T.text,fontWeight:600,fontSize:13}}>{e.subject}</div>
                  <div style={{color:T.textDim,fontSize:11,marginLeft:10,flexShrink:0}}>{e.at}</div>
                </div>
                <div style={{color:T.textSub,fontSize:12,lineHeight:1.5}}>{e.body.slice(0,130)}…</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Email Log ─────────────────────────────────────────────────────────────────
function EmailLogView({T,darkMode,emailLog}){
  if(!emailLog.length) return(
    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:T.textDim,gap:4}}>
      <div style={{fontSize:48,marginBottom:8,opacity:0.5}}>✉</div>
      <div style={{fontSize:16,color:T.textSub,fontWeight:600}}>No emails logged yet</div>
      <div style={{fontSize:13,marginTop:4,color:T.textDim}}>Compose an email from any lead to see it here.</div>
    </div>
  );
  return(
    <div style={{flex:1,overflow:"auto",padding:"20px 24px",display:"flex",flexDirection:"column",gap:12}}>
      {emailLog.map(e=>{
        const cat=getCat(e.cat);
        return(
          <div key={e.id} style={{background:T.card,borderRadius:12,padding:20,border:`1px solid ${T.border}`,boxShadow:T.cardShadow}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,paddingBottom:12,borderBottom:`1px solid ${T.borderSub}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:10,fontWeight:600,color:"#fff",padding:"2px 9px",borderRadius:20,background:cat.color,letterSpacing:"0.04em",textTransform:"uppercase"}}>{cat.icon} {cat.label}</span>
                <div>
                  <div style={{color:GOLD,fontWeight:700,fontSize:14}}>{e.company}</div>
                  <div style={{color:T.text,fontSize:13,marginTop:2}}>{e.subject}</div>
                </div>
              </div>
              <div style={{color:T.textDim,fontSize:11,flexShrink:0,marginLeft:12}}>{e.at}</div>
            </div>
            <div style={{color:T.textSub,fontSize:13,lineHeight:1.75,whiteSpace:"pre-wrap"}}>{e.body}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Email Modal ───────────────────────────────────────────────────────────────
function EmailModal({T,darkMode,lead,onClose,onSend,emailSettings={},userEmail,notify}){
  const tmpl=EMAIL_TEMPLATES[lead.cat]||EMAIL_TEMPLATES.restaurant;
  const [subject,setSubject]=useState(tmpl.subject);
  const [body,setBody]=useState(tmpl.body.replace("[Contact]",lead.contact.split(" ")[0]));
  const [prompt,setPrompt]=useState("");
  const [gen,setGen]=useState(false);
  const [err,setErr]=useState("");
  const [copied,setCopied]=useState(false);
  const [sending,setSending]=useState(false);
  const [testSending,setTestSending]=useState(false);
  const [testEmail,setTestEmail]=useState(userEmail||"");
  const inp={width:"100%",padding:"9px 12px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",outline:"none"};

  const generate=async()=>{
    if(!prompt.trim())return;
    if(!emailSettings?.claudeKey){setErr("Add your Claude API key in Settings → ✦ AI Settings first.");return;}
    setGen(true);setErr("");
    try{
      const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","x-api-key":emailSettings?.claudeKey||"","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1000,
          system:`You are a professional sales copywriter for Elite Cravings Bakehouse — an artisan commercial bakery in Estero, Florida. Products: fresh croissants, sourdough, baguettes, brioche, pastries, rolls. Always fresh, steam-baked, delivered daily to wholesale clients across SW Florida. Write warm, confident emails under 200 words with one clear call to action. Return ONLY the email body.`,
          messages:[{role:"user",content:`Prospect: ${lead.company} | ${lead.contact} | ${getCat(lead.cat).label} | Stage: ${getStage(lead.stage).label}\nNotes: ${lead.notes}\nInstruction: ${prompt}`}]})});
      const data=await res.json();
      const text=data.content?.map(b=>b.text||"").join("")||"";
      if(text)setBody(text.trim());else setErr("No response — try rephrasing.");
    }catch{setErr("Generation failed.");}
    setGen(false);
  };

  const copyAll=()=>{
    navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`).catch(()=>{});
    setCopied(true); setTimeout(()=>setCopied(false),2000);
  };
  const openMailto=()=>{
    window.open(`mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };
  const sendViaApi=async()=>{
    if(!emailSettings?.apiEndpoint)return;
    setSending(true);setErr("");
    try{
      const res=await fetch(emailSettings.apiEndpoint,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({to:lead.email,subject,body,fromName:emailSettings.fromName||"Elite Cravings Bakehouse",fromEmail:emailSettings.fromEmail||""})});
      if(res.ok){onSend(subject,body);}else{setErr(`Send failed (${res.status}). Check your endpoint in Settings.`);}
    }catch{setErr("Could not reach the API endpoint. Check Settings.");}
    setSending(false);
  };
  const sendTest=async()=>{
    if(!emailSettings?.apiEndpoint||!testEmail.trim())return;
    setTestSending(true);setErr("");
    try{
      const res=await fetch(emailSettings.apiEndpoint,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({to:testEmail.trim(),subject:`[TEST] ${subject}`,body,html:`<div style="font-family:Arial,sans-serif;max-width:600px;line-height:1.75"><div style="background:#FFF3CD;border:1px solid #FFEAA7;border-radius:8px;padding:12px 16px;margin-bottom:20px;color:#856404;font-size:13px"><strong>⚠ TEST EMAIL</strong> — This is a preview of the email that will be sent to <strong>${lead.company}</strong> (${lead.email})</div>${body.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br/>")}</div>`,fromName:emailSettings.fromName||"Elite Cravings Bakehouse",fromEmail:emailSettings.fromEmail||""})});
      if(res.ok){notify&&notify("Test email sent to "+testEmail,"success");}
      else{setErr(`Test send failed (${res.status}).`);}
    }catch{setErr("Could not reach the API endpoint.");}
    setTestSending(false);
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1000,backdropFilter:"blur(8px)",animation:"fadeIn .2s ease",overflowY:"auto",padding:"20px 0"}} onClick={onClose}>
      <div style={{background:T.modalBg,borderRadius:16,border:`1px solid ${T.border}`,width:"92%",maxWidth:640,margin:"auto",boxShadow:`0 24px 64px rgba(0,0,0,${darkMode?".7":".2"})`,animation:"slideIn .25s ease"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"16px 20px 12px",borderBottom:`1px solid ${T.border}`,background:darkMode?"rgba(201,168,76,0.02)":"rgba(201,168,76,0.02)",borderRadius:"16px 16px 0 0"}}>
          <div>
            <div style={{color:GOLD,fontSize:16,fontWeight:800,letterSpacing:"-0.02em"}}>Compose Email</div>
            <div style={{color:T.textSub,fontSize:11,marginTop:3}}>To: <strong style={{color:T.text}}>{lead.company}</strong> — {lead.contact}</div>
          </div>
          <button style={{background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)",border:"none",color:T.textSub,cursor:"pointer",fontSize:14,padding:"4px 8px",borderRadius:6,transition:"background .15s"}} onClick={onClose}>✕</button>
        </div>

        <div style={{padding:"10px 20px",background:darkMode?"rgba(201,168,76,0.04)":"rgba(201,168,76,0.06)",borderBottom:`1px solid ${T.border}`}}>
          <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:6}}>✦ AI Generator</div>
          <div style={{display:"flex",gap:8}}>
            <input style={{...inp,flex:1}} placeholder='e.g. "follow-up after sample"'
              value={prompt} onChange={e=>setPrompt(e.target.value)} onKeyDown={e=>e.key==="Enter"&&generate()}/>
            <button className="gbtn" style={{padding:"8px 14px",background:GOLD,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:6,transition:"background .15s"}}
              disabled={gen} onClick={generate}>
              {gen?<Spnr/>:"Generate ✦"}
            </button>
          </div>
          {err&&<div style={{color:RED,fontSize:11,marginTop:5}}>{err}</div>}
        </div>

        <div style={{padding:"14px 20px"}}>
          <div style={{marginBottom:10}}>
            <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:4}}>Subject Line</label>
            <input style={inp} value={subject} onChange={e=>setSubject(e.target.value)}/>
          </div>
          <div>
            <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:4}}>Email Body</label>
            <textarea style={{...inp,minHeight:150,resize:"vertical",lineHeight:1.75,padding:"10px 12px"}} value={body} onChange={e=>setBody(e.target.value)}/>
          </div>
        </div>

        {emailSettings?.apiEndpoint&&<div style={{padding:"8px 20px",background:darkMode?"rgba(59,130,246,0.04)":"rgba(59,130,246,0.04)",borderTop:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{color:"#3B82F6",fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",whiteSpace:"nowrap"}}>🧪 TEST</span>
            <input style={{flex:1,minWidth:120,padding:"6px 10px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.text,fontSize:12,fontFamily:"inherit",boxSizing:"border-box",outline:"none"}} value={testEmail} onChange={e=>setTestEmail(e.target.value)} placeholder="test@yourdomain.com"/>
            <button className="abtn" disabled={testSending||!testEmail.trim()} style={{padding:"6px 14px",background:"none",border:"1px solid #3B82F6",borderRadius:6,color:"#3B82F6",cursor:"pointer",fontFamily:"inherit",fontSize:12,display:"flex",alignItems:"center",gap:5,transition:"all .15s",whiteSpace:"nowrap",opacity:testSending||!testEmail.trim()?0.5:1}} onClick={sendTest}>{testSending?<><Spnr/>&nbsp;Sending…</>:"Send Test"}</button>
          </div>
        </div>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,padding:"12px 20px",flexWrap:"wrap",borderRadius:"0 0 16px 16px"}}>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <button className="abtn" style={{padding:"7px 12px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:6,color:copied?GREEN_OK:T.textSub,cursor:"pointer",fontFamily:"inherit",fontSize:12,transition:"all .15s"}} onClick={copyAll}>{copied?"✓ Copied":"📋 Copy"}</button>
            <button className="abtn" style={{padding:"7px 12px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.textSub,cursor:"pointer",fontFamily:"inherit",fontSize:12,transition:"all .15s"}} onClick={openMailto}>📧 Mail App</button>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            <button style={{padding:"7px 14px",background:"none",border:`1px solid ${T.border}`,borderRadius:6,color:T.textSub,cursor:"pointer",fontFamily:"inherit",fontSize:12}} onClick={onClose}>Cancel</button>
            {emailSettings?.apiEndpoint&&<button className="abtn" disabled={sending} style={{padding:"7px 14px",background:"none",border:`1px solid ${GOLD}`,borderRadius:6,color:GOLD,cursor:"pointer",fontFamily:"inherit",fontSize:12,display:"flex",alignItems:"center",gap:5,transition:"all .15s"}} onClick={sendViaApi}>{sending?<><Spnr/>&nbsp;</>:"📤 "}{sending?"Sending…":"Send Email"}</button>}
            <button className="gbtn" style={{padding:"7px 16px",background:GOLD,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit",transition:"background .15s"}} onClick={()=>onSend(subject,body)}>📬 Log Sent</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Add Lead Modal ────────────────────────────────────────────────────────────
function AddModal({T,darkMode,isMobile,onClose,onAdd,teamUsers=[]}){
  const [f,setF]=useState({company:"",contact:"",email:"",phone:"",cat:"restaurant",priority:"med",address:"",notes:"",assignedTo:"",assignedName:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const inp={width:"100%",padding:"10px 13px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:8,color:T.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",outline:"none",transition:"border-color .2s"};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1000,backdropFilter:"blur(8px)",animation:"fadeIn .2s ease",overflowY:"auto",padding:"20px 0"}} onClick={onClose}>
      <div style={{background:T.modalBg,borderRadius:16,border:`1px solid ${T.border}`,width:"92%",maxWidth:520,margin:"auto",boxShadow:`0 24px 64px rgba(0,0,0,${darkMode?".7":".2"})`,animation:"slideIn .25s ease"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px 12px",borderBottom:`1px solid ${T.border}`,borderRadius:"16px 16px 0 0"}}>
          <div style={{color:GOLD,fontSize:17,fontWeight:800,letterSpacing:"-0.02em"}}>Add New Lead</div>
          <button style={{background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)",border:"none",color:T.textSub,cursor:"pointer",fontSize:14,padding:"4px 8px",borderRadius:6}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"14px 20px",display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
          {[["company","Company *",2],["contact","Contact Title",1],["email","Email",1],["phone","Phone",1],["address","Address",2]].map(([k,lb,span])=>(
            <div key={k} style={{gridColumn:`span ${span}`}}>
              <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:5}}>{lb}</label>
              <input style={inp} value={f[k]} onChange={e=>set(k,e.target.value)}/>
            </div>
          ))}
          <div>
            <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:5}}>Category</label>
            <select style={inp} value={f.cat} onChange={e=>set("cat",e.target.value)}>
              {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:5}}>Priority</label>
            <select style={inp} value={f.priority} onChange={e=>set("priority",e.target.value)}>
              {["high","med","low"].map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {teamUsers.length>0&&<div>
            <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:5}}>Assign To</label>
            <select style={inp} value={f.assignedTo} onChange={e=>{const u=teamUsers.find(u=>u.uid===e.target.value);set("assignedTo",e.target.value);set("assignedName",u?u.displayName||u.email:"");}}>
              <option value="">Unassigned</option>
              {teamUsers.map(u=><option key={u.uid} value={u.uid}>{u.displayName||u.email}</option>)}
            </select>
          </div>}
          <div style={{gridColumn:"span 2"}}>
            <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:5}}>Notes</label>
            <textarea style={{...inp,height:68,resize:"vertical"}} value={f.notes} onChange={e=>set("notes",e.target.value)}/>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,padding:"13px 22px",borderTop:`1px solid ${T.border}`}}>
          <button style={{padding:"9px 18px",background:"none",border:`1px solid ${T.border}`,borderRadius:6,color:T.textSub,cursor:"pointer",fontFamily:"inherit",fontSize:13}} onClick={onClose}>Cancel</button>
          <button className="gbtn" style={{padding:"9px 20px",background:GOLD,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",transition:"background .15s"}}
            onClick={()=>{if(f.company.trim()){onAdd(f);onClose();}}}>Add to Pipeline</button>
        </div>
      </div>
    </div>
  );
}

// ── Micro ─────────────────────────────────────────────────────────────────────
const Toast = ({T,darkMode,msg,type}) => (
  <div style={{position:"fixed",bottom:28,right:28,padding:"14px 22px",borderRadius:12,
    border:`1px solid ${type==="error"?RED+"44":GOLD+"44"}`,
    background:type==="error"?(darkMode?"rgba(239,68,68,0.12)":"#FFF0F0"):(darkMode?"rgba(201,168,76,0.08)":"#FFFDF5"),
    color:type==="error"?RED:T.text,fontSize:13,fontFamily:"inherit",zIndex:2000,fontWeight:500,
    boxShadow:`0 12px 40px rgba(0,0,0,${darkMode?"0.4":"0.12"})`,animation:"fadeUp .3s ease",
    backdropFilter:"blur(12px)",display:"flex",alignItems:"center",gap:8}}>
  <span style={{fontSize:16}}>{type==="error"?"⚠":"✓"}</span>{msg}</div>
);
const Spnr = () => <span style={{display:"inline-block",width:14,height:14,border:`2px solid ${GOLD}44`,borderTopColor:GOLD,borderRadius:"50%",animation:"spin .6s linear infinite"}}/>;

// ── Lead Locator ──────────────────────────────────────────────────────────────
const CONTACT_DEFAULTS = {
  restaurant:"Executive Chef / F&B Director",
  hospital:"Food Service Director",
  senior:"Dining Services Manager",
  club:"Executive Chef",
  foodservice:"Procurement Manager",
  // extra cats
  catering:"Director of Catering",
  corporate:"Office Manager / Facilities",
  school:"Food Service Director",
  government:"Facilities Manager",
  cityhall:"Administrative Manager",
  emergency:"Station Commander",
  library:"Library Director",
  hotel:"F&B Manager",
  stadium:"Catering / Events Director",
};

function LeadLocatorView({T,darkMode,isMobile,leads,onAdd,emailSettings={}}){
  const [zip,setZip]=useState(emailSettings.areaZip||"");
  const [radius,setRadius]=useState("20");
  const [catFilter,setCatFilter]=useState("all");
  const [results,setResults]=useState([]);
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const [searched,setSearched]=useState(false);
  const [added,setAdded]=useState(new Set());
  const [pinned,setPinned]=useState(()=>{try{return JSON.parse(localStorage.getItem('ec_pinned')||'[]');}catch{return[];}});
  const [analysis,setAnalysis]=useState("");
  const [analyzing,setAnalyzing]=useState(false);
  const [showAnalysis,setShowAnalysis]=useState(false);
  const [analysisErr,setAnalysisErr]=useState("");
  const [showMap,setShowMap]=useState(false);
  const geoCache=useRef({});
  const resultCache=useRef({});

  // Persist pins
  useEffect(()=>{try{localStorage.setItem('ec_pinned',JSON.stringify(pinned));}catch{}},[pinned]);
  // Load persisted result cache on mount
  useEffect(()=>{try{const s=localStorage.getItem('ec_locator_cache');if(s)resultCache.current=JSON.parse(s);}catch{}},[]);

  const togglePin=r=>setPinned(prev=>prev.some(p=>p.osmId===r.osmId)?prev.filter(p=>p.osmId!==r.osmId):[...prev,r]);
  const isPinned=osmId=>pinned.some(p=>p.osmId===osmId);

  const haversine=(lat1,lng1,lat2,lng2)=>{
    const R=3959,dLat=(lat2-lat1)*Math.PI/180,dLng=(lng2-lng1)*Math.PI/180;
    const a=Math.sin(dLat/2)**2+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
    return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
  };

  const toCat=tags=>{
    const a=tags.amenity||"",l=tags.leisure||"",sf=tags.social_facility||"",sh=tags.shop||"",n=(tags.name||"").toLowerCase();
    if(/^(restaurant|cafe|fast_food|bar|pub|food_court|biergarten)$/.test(a))return"restaurant";
    if(/^(hospital|clinic|doctors|healthcare)$/.test(a))return"hospital";
    if(/^(nursing_home|retirement_home|social_facility)$/.test(a)||/nursing_home|assisted_living/.test(sf)||(/senior|retirement|assisted/.test(n)&&/living|community|care/.test(n)))return"senior";
    if(/golf_course/.test(l)||/country.?club|golf.?club/i.test(n))return"club";
    if(/^(wholesale|supermarket|deli|bakery|food)$/.test(sh))return"foodservice";
    return null;
  };

  const toExtraCat=(tags,enabledIds)=>{
    const a=tags.amenity||"",o=tags.office||"",g=tags.government||"",sh=tags.shop||"",t=tags.tourism||"",le=tags.leisure||"";
    const try_=(id,test)=>enabledIds.includes(id)&&test?id:null;
    return(
      try_("catering",  /^catering$/.test(sh)||!!tags.catering)||
      try_("corporate",  /^(company|corporation|financial|it|logistics|insurance|consulting)$/.test(o))||
      try_("school",    /^(school|university|college|kindergarten)$/.test(a))||
      try_("government",o==="government"||!!g)||
      try_("cityhall",  /^(townhall|courthouse)$/.test(a))||
      try_("emergency", /^(police|fire_station)$/.test(a))||
      try_("library",   a==="library")||
      try_("hotel",     /^(hotel|motel|guest_house|hostel)$/.test(t))||
      try_("stadium",   /^(stadium|sports_centre|sports_hall)$/.test(le)||a==="events_venue")||
      null
    );
  };

  const search=async()=>{
    if(!zip.trim()){setErr("Please enter a zip code.");return;}
    // Return cached results instantly
    const cacheKey=`${zip.trim()}-${radius}-${catFilter}`;
    if(resultCache.current[cacheKey]){
      setResults(resultCache.current[cacheKey]);
      setSearched(true);
      return;
    }
    setLoading(true);setErr("");setResults([]);setSearched(false);
    try{
      // 1. Geocode zip (cached per ZIP)
      let lat,lng;
      if(geoCache.current[zip.trim()]){
        ({lat,lng}=geoCache.current[zip.trim()]);
      }else{
        const geoRes=await fetch(
          `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(zip.trim())}&country=US&format=json&limit=1`,
          {headers:{"User-Agent":"ECB-CRM-LeadLocator/1.0","Accept-Language":"en"}}
        );
        const geoData=await geoRes.json();
        if(!geoData.length){setErr("Zip code not found. Try another.");setLoading(false);return;}
        lat=parseFloat(geoData[0].lat);lng=parseFloat(geoData[0].lon);
        geoCache.current[zip.trim()]={lat,lng};
      }

      // 2. Build Overpass QL query using nwr (nodes+ways+relations in one pass)
      const meters=Math.round(parseInt(radius)*1609.34);
      const ar=`(around:${meters},${lat},${lng})`;
      const parts=[];
      const add=f=>{parts.push(`nwr${f}${ar};`);};
      const c=catFilter;
      const enabledExtras=emailSettings?.locatorExtras||[];
      if(c==="all"||c==="restaurant")add(`["amenity"~"^(restaurant|cafe|fast_food|bar|pub|food_court)$"]`);
      if(c==="all"||c==="hospital")add(`["amenity"~"^(hospital|clinic|doctors|healthcare)$"]`);
      if(c==="all"||c==="senior"){add(`["amenity"~"^(nursing_home|retirement_home)$"]`);add(`["social_facility"]`);}
      if(c==="all"||c==="club")add(`["leisure"="golf_course"]`);
      if(c==="all"||c==="foodservice")add(`["shop"~"^(wholesale|supermarket|deli|bakery|food)$"]`);
      // Extra user-enabled categories
      EXTRA_CATS.filter(ec=>enabledExtras.includes(ec.id)).forEach(ec=>{
        if(c==="all"||c===ec.id) ec.osmQ.forEach(q=>add(q));
      });
      const query=`[out:json][timeout:20];\n(\n${parts.join("\n")}\n);\nout center 150;`;

      // 3. Query Overpass API
      const ovRes=await fetch("https://overpass-api.de/api/interpreter",{method:"POST",body:query});
      const ovData=await ovRes.json();

      // 4. Parse + dedupe + categorize results
      const seen=new Set();
      const processed=[];
      for(const el of ovData.elements||[]){
        const tags=el.tags||{};
        const name=tags.name;
        if(!name||seen.has(name.toLowerCase()))continue;
        seen.add(name.toLowerCase());
        const elLat=el.lat??el.center?.lat;
        const elLng=el.lon??el.center?.lon;
        if(!elLat||!elLng)continue;
        const cat=toCat(tags)||(enabledExtras.length?toExtraCat(tags,enabledExtras):null);
        if(!cat)continue;
        if(catFilter!=="all"&&cat!==catFilter)continue;
        const addr=[tags["addr:housenumber"],tags["addr:street"],tags["addr:city"],tags["addr:state"]].filter(Boolean).join(" ")||tags["addr:full"]||"";
        processed.push({
          osmId:`${el.type}/${el.id}`,name,cat,
          address:addr,lat:elLat,lng:elLng,
          phone:tags.phone||tags["contact:phone"]||"",
          email:tags.email||tags["contact:email"]||"",
          miles:haversine(lat,lng,elLat,elLng),
        });
      }
      processed.sort((a,b)=>a.miles-b.miles);
      resultCache.current[cacheKey]=processed;
      const cacheKeys=Object.keys(resultCache.current);
      if(cacheKeys.length>5)delete resultCache.current[cacheKeys[0]];
      try{localStorage.setItem('ec_locator_cache',JSON.stringify(resultCache.current));}catch{}
      setResults(processed);
    }catch{setErr("Search failed. Check your connection and try again.");}
    setLoading(false);setSearched(true);
  };

  const addResult=async r=>{
    const extraDef=EXTRA_CATS.find(c=>c.id===r.cat);
    const pipelineCat=extraDef?extraDef.pipelineCat:r.cat;
    await onAdd({
      company:r.name,
      contact:CONTACT_DEFAULTS[r.cat]||"",
      email:r.email,phone:r.phone,cat:pipelineCat,
      priority:"med",address:r.address,
      notes:`Found via Lead Locator (${r.miles.toFixed(1)} mi). Category: ${getAnyCat(r.cat).label}. Source: OpenStreetMap.`,
    });
    setAdded(prev=>new Set([...prev,r.osmId]));
  };

  const runAnalysis=async()=>{
    if(!emailSettings?.claudeKey){
      setAnalysisErr("No Claude API key found. Go to Settings → ✦ AI Settings and paste your key, then save.");
      setAnalysis("");setShowAnalysis(true);
      return;
    }
    setAnalyzing(true);setAnalysisErr("");setAnalysis("");setShowAnalysis(true);
    try{
      const list=results.slice(0,60).map((r,i)=>
        `${i+1}. ${r.name} (${getCat(r.cat).label}, ${r.miles.toFixed(1)} mi)${r.address?` — ${r.address}`:""}`
      ).join("\n");
      const res=await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json","x-api-key":emailSettings?.claudeKey||"","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",max_tokens:1400,
          system:`You are a strategic sales advisor for Elite Cravings Bakehouse, a commercial artisan bakery in Estero, FL. Products: fresh croissants, sourdough, baguettes, brioche, pastries, dinner rolls — steam-baked daily, delivered across SW Florida. Identify the strongest commercial bakery supply opportunities. Be specific, tactical, and direct.`,
          messages:[{role:"user",content:`Analyze these ${results.length} nearby businesses for commercial bakery marketing. ZIP: ${zip}, radius: ${radius} miles.\n\n${list}\n\nProvide:\n1. Top 5 priority targets — name each one and explain why in 1-2 sentences\n2. Strongest category in this area and why\n3. One local outreach tactic specific to this market\n\nBe concise and specific. Plain text only, no markdown.`}]
        })
      });
      const data=await res.json();
      const text=data.content?.map(b=>b.text||"").join("")||"";
      if(text)setAnalysis(text.trim());
      else setAnalysisErr(data.error?.message||"No response — verify Claude API key in Settings.");
    }catch{setAnalysisErr("Analysis failed. Verify your Claude API key in Settings.");}
    setAnalyzing(false);
  };

  const inp={padding:"8px 12px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none"};
  const existingNames=new Set(leads.map(l=>l.company.toLowerCase()));

  return(
    <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
      {showMap&&<MapModal T={T} darkMode={darkMode} leads={results.map(r=>({...r,company:r.name}))} onClose={()=>setShowMap(false)} title="Locator Map" areaZip={emailSettings.areaZip||""}/>}

      {/* Search controls */}
      <div style={{padding:isMobile?"10px 14px":"14px 24px",borderBottom:`1px solid ${T.border}`,background:T.surface,display:"flex",gap:isMobile?8:10,flexWrap:"wrap",alignItems:"center",flexShrink:0,transition:"background .3s ease"}}>
        <input style={{...inp,width:isMobile?"100%":120}} placeholder="ZIP Code" value={zip}
          onChange={e=>setZip(e.target.value)} onKeyDown={e=>e.key==="Enter"&&search()}/>
        <select style={inp} value={[5,10,20,30,50].includes(Number(radius)) ? radius : "custom"}
          onChange={e=>{ if(e.target.value!=="custom") setRadius(e.target.value); else setRadius(""); }}>
          {[5,10,20,30,50].map(r=><option key={r} value={r}>{r} miles</option>)}
          <option value="custom">Custom…</option>
        </select>
        {![5,10,20,30,50].includes(Number(radius))&&(
          <input type="number" style={{...inp,width:isMobile?70:90}} placeholder="mi" value={radius}
            min={1} max={500} onChange={e=>setRadius(e.target.value)}/>
        )}
        <select style={inp} value={catFilter} onChange={e=>setCatFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          {(emailSettings?.locatorExtras||[]).map(id=>{const ec=EXTRA_CATS.find(c=>c.id===id);return ec?<option key={ec.id} value={ec.id}>{ec.icon} {ec.label}</option>:null;})}
        </select>
        <button className="gbtn"
          style={{padding:"8px 20px",background:loading?"#888":GOLD,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:13,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:7,transition:"background .15s"}}
          onClick={search} disabled={loading}>
          {loading?<><Spnr/>&nbsp;Searching…</>:"🔍 Search"}
        </button>
        {results.length>0&&(
          <button className="gbtn"
            style={{padding:"8px 16px",background:GOLD,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:7,transition:"background .15s"}}
            onClick={()=>setShowMap(true)}>
            🗺 Map it!
          </button>
        )}
        {results.length>0&&(
          <button className="abtn"
            style={{padding:"8px 16px",background:"none",border:`1px solid ${GOLD}`,borderRadius:6,color:GOLD,fontWeight:700,fontSize:13,cursor:analyzing?"not-allowed":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:7,transition:"all .15s"}}
            onClick={runAnalysis} disabled={analyzing}>
            {analyzing?<><Spnr/>&nbsp;Analyzing…</>:"✦ Run Analysis"}
          </button>
        )}
        {err&&<span style={{color:RED,fontSize:12,marginLeft:4}}>{err}</span>}
      </div>

      {/* Results area */}
      <div style={{flex:1,overflow:"auto",padding:"16px 22px"}}>

        {/* Pinned leads */}
        {pinned.length>0&&(
          <div style={{marginBottom:22}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <span style={{color:GOLD,fontWeight:700,fontSize:13}}>📌 Pinned</span>
              <span style={{color:T.textDim,fontSize:12}}>({pinned.length})</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12}}>
              {pinned.map(r=>{
                const cat=getCat(r.cat);
                const inPipeline=existingNames.has(r.name.toLowerCase())||added.has(r.osmId);
                return(
                  <div key={r.osmId}
                    style={{background:T.card,borderRadius:10,border:`2px solid ${GOLD}55`,aspectRatio:"1",display:"flex",flexDirection:"column",padding:14,overflow:"hidden"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div style={{width:44,height:44,borderRadius:10,background:cat.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:`0 2px 8px ${cat.color}44`}}>
                        {cat.icon}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
                        <button onClick={()=>togglePin(r)} title="Unpin"
                          style={{background:"none",border:"none",cursor:"pointer",padding:"1px 3px",fontSize:14,lineHeight:1,color:GOLD}}>
                          📌
                        </button>
                        <span style={{color:T.textSub,fontSize:11,fontWeight:600}}>{r.miles.toFixed(1)} mi</span>
                      </div>
                    </div>
                    <div style={{flex:1,overflow:"hidden"}}>
                      <div style={{color:T.text,fontWeight:700,fontSize:13,lineHeight:1.3,marginBottom:4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{r.name}</div>
                      <span style={{fontSize:9,fontWeight:600,color:"#fff",padding:"2px 8px",borderRadius:20,background:cat.color,letterSpacing:"0.04em",textTransform:"uppercase"}}>{cat.label}</span>
                      {r.address&&<div style={{color:T.textSub,fontSize:11,marginTop:6,lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>📍 {r.address}</div>}
                    </div>
                    <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.borderSub}`}}>
                      {inPipeline
                        ?<span style={{fontSize:11,color:GREEN_OK,fontWeight:700}}>✓ In Pipeline</span>
                        :<button className="gbtn" style={{width:"100%",padding:"6px 0",background:GOLD,border:"none",borderRadius:6,color:"#000",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"background .15s"}} onClick={()=>addResult(r)}>+ Add Lead</button>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!searched&&!loading&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",gap:10,color:T.textDim}}>
            <div style={{fontSize:48}}>📍</div>
            <div style={{fontSize:15,color:T.textSub,fontWeight:600}}>Find new leads by location</div>
            <div style={{fontSize:13,color:T.textDim,textAlign:"center",maxWidth:380,lineHeight:1.65}}>
              Enter a ZIP code and search radius to discover nearby restaurants, hospitals, senior living communities, country clubs, and food service accounts.
            </div>
            <div style={{marginTop:8,padding:"10px 16px",borderRadius:7,border:`1px solid ${T.borderSub}`,background:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",fontSize:12,color:T.textDim}}>
              Powered by OpenStreetMap · No API key required
            </div>
          </div>
        )}

        {/* No results */}
        {searched&&!loading&&results.length===0&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",gap:8}}>
            <div style={{fontSize:38}}>🗺</div>
            <div style={{fontSize:14,color:T.textSub,fontWeight:600}}>No results found</div>
            <div style={{fontSize:12,color:T.textDim,textAlign:"center",maxWidth:360,lineHeight:1.6}}>
              Try a larger radius or different category. Map data comes from OpenStreetMap and coverage varies by area.
            </div>
          </div>
        )}

        {/* Results list */}
        {results.length>0&&(
          <>
            <div style={{color:T.textSub,fontSize:12,marginBottom:14}}>
              <span style={{color:GOLD,fontWeight:700,fontSize:15}}>{results.length}</span>
              <span style={{marginLeft:6}}>potential leads near</span>
              <strong style={{color:T.text,marginLeft:4}}>{zip}</strong>
              <span style={{marginLeft:4}}>within</span>
              <strong style={{color:T.text,marginLeft:4}}>{radius} miles</strong>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12}}>
              {results.map(r=>{
                const cat=getCat(r.cat);
                const inPipeline=existingNames.has(r.name.toLowerCase())||added.has(r.osmId);
                return(
                  <div key={r.osmId}
                    style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,aspectRatio:"1",display:"flex",flexDirection:"column",padding:14,transition:"border-color .2s,transform .2s,box-shadow .2s",overflow:"hidden",boxShadow:T.cardShadow,cursor:"default"}}>
                    {/* Top: icon + pin + distance */}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div style={{width:44,height:44,borderRadius:10,background:`linear-gradient(135deg,${cat.color},${cat.color}cc)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:`0 3px 12px ${cat.color}33`}}>
                        {cat.icon}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
                        <button onClick={()=>togglePin(r)} title={isPinned(r.osmId)?"Unpin":"Pin"}
                          style={{background:"none",border:"none",cursor:"pointer",padding:"1px 3px",fontSize:14,lineHeight:1,color:isPinned(r.osmId)?GOLD:T.textDim,transition:"color .15s"}}>
                          📌
                        </button>
                        <span style={{color:T.textSub,fontSize:11,fontWeight:600}}>{r.miles.toFixed(1)} mi</span>
                      </div>
                    </div>
                    {/* Name + address */}
                    <div style={{flex:1,overflow:"hidden"}}>
                      <div style={{color:T.text,fontWeight:700,fontSize:13,lineHeight:1.3,marginBottom:4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{r.name}</div>
                      <span style={{fontSize:9,fontWeight:600,color:"#fff",padding:"2px 8px",borderRadius:20,background:cat.color,letterSpacing:"0.04em",textTransform:"uppercase"}}>{cat.label}</span>
                      {r.address&&<div style={{color:T.textSub,fontSize:11,marginTop:6,lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>📍 {r.address}</div>}
                    </div>
                    {/* Bottom: action */}
                    <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.borderSub}`}}>
                      {inPipeline
                        ?<span style={{fontSize:11,color:GREEN_OK,fontWeight:700}}>✓ In Pipeline</span>
                        :<button className="gbtn" style={{width:"100%",padding:"6px 0",background:GOLD,border:"none",borderRadius:6,color:"#000",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"background .15s"}} onClick={()=>addResult(r)}>+ Add Lead</button>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

      </div>

      {/* Analysis modal */}
      {showAnalysis&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,backdropFilter:"blur(6px)"}} onClick={()=>setShowAnalysis(false)}>
          <div style={{background:darkMode?"#111":"#fff",borderRadius:14,border:`1px solid ${GOLD}44`,width:"92%",maxWidth:580,maxHeight:"82vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:`0 24px 70px rgba(0,0,0,${darkMode?".7":".18"})`}} onClick={e=>e.stopPropagation()}>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"18px 22px 14px",borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.08)"}`}}>
              <div>
                <div style={{color:GOLD,fontSize:16,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>✦ Market Analysis</div>
                <div style={{color:darkMode?"#888":"#999",fontSize:12,marginTop:3}}>
                  {zip} · {radius} mi · {results.length} prospects analyzed
                </div>
              </div>
              <button style={{background:"none",border:"none",color:darkMode?"#666":"#aaa",cursor:"pointer",fontSize:18,padding:0}} onClick={()=>setShowAnalysis(false)}>✕</button>
            </div>
            {/* Body */}
            <div style={{padding:"18px 22px",overflow:"auto",flex:1,minHeight:0}}>
              {analyzing&&(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:"40px 0",color:darkMode?"#666":"#aaa"}}>
                  <Spnr/>
                  <div style={{fontSize:13}}>Analyzing {results.length} businesses…</div>
                </div>
              )}
              {!analyzing&&analysis&&(
                <p style={{color:darkMode?"#E8E0D0":"#222",fontSize:13,lineHeight:1.85,whiteSpace:"pre-wrap",margin:0}}>{analysis}</p>
              )}
              {!analyzing&&analysisErr&&(
                <div style={{color:RED,fontSize:13,lineHeight:1.6,padding:"14px",background:darkMode?"rgba(239,68,68,0.08)":"rgba(239,68,68,0.06)",borderRadius:8,border:`1px solid ${RED}33`}}>
                  ⚠ {analysisErr}
                </div>
              )}
            </div>
            {/* Footer */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"13px 22px",borderTop:`1px solid ${darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.08)"}`}}>
              <button className="abtn" disabled={analyzing}
                style={{padding:"8px 16px",background:"none",border:`1px solid ${GOLD}`,borderRadius:6,color:GOLD,fontWeight:700,fontSize:13,cursor:analyzing?"not-allowed":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:6,transition:"all .15s"}}
                onClick={runAnalysis}>
                {analyzing?<><Spnr/>&nbsp;Running…</>:"✦ Re-run"}
              </button>
              <button style={{padding:"8px 20px",background:"none",border:`1px solid ${darkMode?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.12)"}`,borderRadius:6,color:darkMode?"#888":"#666",cursor:"pointer",fontFamily:"inherit",fontSize:13}} onClick={()=>setShowAnalysis(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Brand Builder (must be top-level to avoid remount on every keystroke) ──────
const BRAND_ICONS=["🥐","🍞","🎂","🍰","🧁","☕","🌟","⭐","🏆","💎","🔥","✨","🍽️","🌿","🦋","🌸","🍀","⚡","🎯","👑","🏅","🔮","🪄","🫧","🧿","🎖️","🌺","🍁"];
const BRAND_FONTS=[
  {label:"Inter (Default)",  value:"Inter, system-ui, sans-serif"},
  {label:"Georgia",          value:"Georgia, serif"},
  {label:"Palatino",         value:"'Palatino Linotype', Palatino, serif"},
  {label:"Trebuchet MS",     value:"'Trebuchet MS', sans-serif"},
  {label:"Arial Black",      value:"'Arial Black', sans-serif"},
  {label:"Courier New",      value:"'Courier New', monospace"},
  {label:"Impact",           value:"Impact, fantasy, sans-serif"},
];
function BrandBuilder({T,darkMode,label,icon,name,font,color,onIcon,onName,onFont,onColor}){
  const card={background:T.card,borderRadius:14,padding:22,border:`1px solid ${T.border}`,boxShadow:T.cardShadow};
  const lbl={color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:6};
  const inp={width:"100%",padding:"10px 13px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:8,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",transition:"border-color .2s"};
  return(
    <div style={card}>
      <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>{label}</div>
      {/* Preview */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",background:darkMode?"#0D0D0D":"#F5F5F3",borderRadius:8,marginBottom:16,border:`1px solid ${T.border}`}}>
        <span style={{fontSize:26}}>{icon||"🥐"}</span>
        <span style={{fontFamily:font,color:color||GOLD,fontWeight:700,fontSize:15}}>{name||"Brand Name"}</span>
      </div>
      {/* Icon picker */}
      <label style={lbl}>Icon</label>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
        {BRAND_ICONS.map(ic=>(
          <button key={ic} onClick={()=>onIcon(ic)}
            style={{width:36,height:36,fontSize:18,background:icon===ic?`${GOLD}22`:"none",border:`1px solid ${icon===ic?GOLD:T.borderSub}`,borderRadius:7,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s"}}>
            {ic}
          </button>
        ))}
        <button onClick={()=>onIcon("")}
          style={{width:36,height:36,fontSize:10,fontWeight:700,background:!icon?`${GOLD}22`:"none",border:`1px solid ${!icon?GOLD:T.borderSub}`,borderRadius:7,cursor:"pointer",color:T.textDim,transition:"all .12s"}}>
          ✕
        </button>
      </div>
      {/* Name */}
      <div style={{marginBottom:12}}>
        <label style={lbl}>Name</label>
        <input style={inp} value={name} onChange={e=>onName(e.target.value)} placeholder="Brand Name"/>
      </div>
      {/* Font */}
      <div style={{marginBottom:12}}>
        <label style={lbl}>Font</label>
        <select style={{...inp,fontFamily:font}} value={font} onChange={e=>onFont(e.target.value)}>
          {BRAND_FONTS.map(f=><option key={f.value} value={f.value} style={{fontFamily:f.value}}>{f.label}</option>)}
        </select>
      </div>
      {/* Color */}
      <div>
        <label style={lbl}>Color</label>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {["#C9A84C","#FFFFFF","#E63946","#2196F3","#4CAF50","#FF9800","#9C27B0","#00BCD4","#FF5722","#607D8B"].map(c=>(
            <button key={c} onClick={()=>onColor(c)}
              style={{width:26,height:26,borderRadius:"50%",background:c,border:color===c?`2px solid ${T.text}`:`2px solid transparent`,cursor:"pointer",flexShrink:0,transition:"border .1s"}}/>
          ))}
          <input type="color" value={color||"#C9A84C"} onChange={e=>onColor(e.target.value)}
            style={{width:32,height:32,padding:2,borderRadius:6,border:`1px solid ${T.borderSub}`,background:"none",cursor:"pointer"}}/>
        </div>
      </div>
    </div>
  );
}

// ── Admin Settings ─────────────────────────────────────────────────────────────
// ── Sample Pack Modal ─────────────────────────────────────────────────────────
function SamplePackModal({T,darkMode,emailSettings,modal,onConfirm,onSkip,onCancel}){
  const packs=emailSettings.samplePacks||[];
  const [sel,setSel]=useState(packs[0]?.id||"");
  const pack=packs.find(p=>p.id===sel);
  const overlay={position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,backdropFilter:"blur(8px)",animation:"fadeIn .2s ease"};
  const box={background:T.modalBg,borderRadius:16,padding:30,width:440,maxWidth:"90vw",border:`1px solid ${T.border}`,boxShadow:`0 24px 64px rgba(0,0,0,${darkMode?"0.7":"0.2"})`,animation:"slideIn .25s ease"};
  return(
    <div style={overlay} onClick={onCancel}>
      <div style={box} onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:22,marginBottom:6,textAlign:"center"}}>📦</div>
        <div style={{color:T.text,fontWeight:700,fontSize:16,textAlign:"center",marginBottom:4}}>Record Sample Sent</div>
        <div style={{color:T.textSub,fontSize:12,textAlign:"center",marginBottom:20}}>Which sample pack was delivered to this lead?</div>
        <label style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:5}}>Sample Pack</label>
        <select value={sel} onChange={e=>setSel(e.target.value)}
          style={{width:"100%",padding:"9px 12px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",marginBottom:14}}>
          {packs.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {pack&&pack.items.length>0&&(
          <div style={{background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",borderRadius:8,padding:"10px 14px",marginBottom:18,border:`1px solid ${T.borderSub}`}}>
            <div style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Contents</div>
            <ul style={{paddingLeft:16,color:T.textSub,fontSize:12,lineHeight:1.8,margin:0}}>
              {pack.items.map((item,i)=><li key={i}>{item}</li>)}
            </ul>
          </div>
        )}
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <button className="abtn" style={{flex:1,padding:"9px 12px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:7,color:T.textSub,fontSize:12,cursor:"pointer",fontFamily:"inherit",transition:"all .15s"}}
            onClick={onCancel}>Cancel</button>
          <button className="abtn" style={{flex:1,padding:"9px 12px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:7,color:T.textSub,fontSize:12,cursor:"pointer",fontFamily:"inherit",transition:"all .15s"}}
            onClick={onSkip}>Skip pack</button>
          <button className="gbtn" style={{flex:1,padding:"9px 12px",background:GOLD,border:"none",borderRadius:7,color:"#000",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"background .15s"}}
            onClick={()=>onConfirm(modal.leadId,{...modal.patch,samplePackId:sel})}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function SettingsView({T,darkMode,isMobile,emailSettings,onSave,notify,isAdmin,can,userProfile,teamUsers=[]}){
  const [form,setForm]=useState({
    fromName:emailSettings.fromName||"Elite Cravings Bakehouse",
    fromEmail:emailSettings.fromEmail||"sales@elitecravings.com",
    apiEndpoint:emailSettings.apiEndpoint||"https://sendemail-e4raa4apka-uc.a.run.app",
    claudeKey:emailSettings.claudeKey||"",
    brevoKey:emailSettings.brevoKey||"",
    mandrillKey:emailSettings.mandrillKey||"",
    ccApiKey:emailSettings.ccApiKey||"",
    mottoText:emailSettings.mottoText||"",
    brand1Icon:emailSettings.brand1Icon||"🥐",
    brand1Name:emailSettings.brand1Name||"Elite Cravings",
    brand1Font:emailSettings.brand1Font||"Inter, system-ui, sans-serif",
    brand1Color:emailSettings.brand1Color||GOLD,
    brand2Icon:emailSettings.brand2Icon||"",
    brand2Name:emailSettings.brand2Name||"Bakehouse · CRM",
    brand2Font:emailSettings.brand2Font||"Inter, system-ui, sans-serif",
    brand2Color:emailSettings.brand2Color||"#888880",
    areaZip:emailSettings.areaZip||"",
  });
  const [locatorExtras,setLocatorExtras]=useState(emailSettings.locatorExtras||[]);
  const toggleExtra=id=>setLocatorExtras(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  const [samplePacks,setSamplePacks]=useState(emailSettings.samplePacks||[]);
  const [newPack,setNewPack]=useState({name:"",items:""});
  const addPack=()=>{
    if(!newPack.name.trim())return;
    setSamplePacks(p=>[...p,{id:Date.now().toString(),name:newPack.name.trim(),items:newPack.items.split("\n").map(s=>s.trim()).filter(Boolean)}]);
    setNewPack({name:"",items:""});
  };
  const [tab,setTab]=useState("company");
  const inp={width:"100%",padding:"10px 13px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:8,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",transition:"border-color .2s"};
  const card={background:T.card,borderRadius:14,padding:22,border:`1px solid ${T.border}`,boxShadow:T.cardShadow};
  const lbl={color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:6};
  const configured=form.apiEndpoint.trim().length>0;
  const payloadStr=`{\n  "to":        "recipient@example.com",\n  "subject":   "Email subject",\n  "body":      "Email body text",\n  "fromName":  "${form.fromName||'Elite Cravings Bakehouse'}",\n  "fromEmail": "${form.fromEmail||'your@email.com'}"\n}`;

  const TABS=[
    {id:"company",  icon:"🏢", label:"Company"},
    {id:"pipeline", icon:"🥐", label:"Pipeline"},
    {id:"email",    icon:"✉",  label:"Email"},
    {id:"campaigns",icon:"📣", label:"Campaigns"},
    {id:"ai",       icon:"✦",  label:"AI & Locator"},
    ...(isAdmin?[{id:"users",icon:"👥",label:"Users"}]:[]),
  ];

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Tab bar */}
      <div style={{display:"flex",gap:2,padding:isMobile?"10px 14px 0":"14px 24px 0",borderBottom:`1px solid ${T.border}`,background:T.topBarBg,flexShrink:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        {TABS.map(t=>{
          const active=tab===t.id;
          return(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:isMobile?"8px 12px":"9px 20px",background:active?T.card:"none",border:`1px solid ${active?T.border:"transparent"}`,borderBottom:active?`2px solid ${T.card}`:`2px solid transparent`,borderTop:active?`2px solid ${GOLD}`:"2px solid transparent",borderRadius:"8px 8px 0 0",color:active?GOLD:T.textSub,fontWeight:active?700:500,fontSize:isMobile?11:13,cursor:"pointer",fontFamily:"inherit",marginBottom:-1,transition:"all .2s ease",display:"flex",alignItems:"center",gap:isMobile?4:7,whiteSpace:"nowrap",flexShrink:0}}>
              <span style={{fontSize:isMobile?12:14}}>{t.icon}</span>{t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{flex:1,overflow:"auto",padding:isMobile?"14px 14px 0":"22px 22px 0"}}>
        <div style={{maxWidth:620,display:"flex",flexDirection:"column",gap:16,paddingBottom:22}}>

          {/* ── COMPANY TAB ── */}
          {tab==="company"&&<>
            <BrandBuilder T={T} darkMode={darkMode} label="🏢 Primary Logo"
              icon={form.brand1Icon} name={form.brand1Name} font={form.brand1Font} color={form.brand1Color}
              onIcon={v=>setForm(p=>({...p,brand1Icon:v}))} onName={v=>setForm(p=>({...p,brand1Name:v}))}
              onFont={v=>setForm(p=>({...p,brand1Font:v}))} onColor={v=>setForm(p=>({...p,brand1Color:v}))}/>
            <BrandBuilder T={T} darkMode={darkMode} label="🏷 Subtitle / Tagline"
              icon={form.brand2Icon} name={form.brand2Name} font={form.brand2Font} color={form.brand2Color}
              onIcon={v=>setForm(p=>({...p,brand2Icon:v}))} onName={v=>setForm(p=>({...p,brand2Name:v}))}
              onFont={v=>setForm(p=>({...p,brand2Font:v}))} onColor={v=>setForm(p=>({...p,brand2Color:v}))}/>
            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>📍 Area Zip Code</div>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65,marginBottom:12}}>Enter your primary area zip code. This will be used as the default center for all maps (Dashboard, Lead Locator, Lead Map).</div>
              <label style={lbl}>Zip Code</label>
              <input style={inp} value={form.areaZip||""} onChange={e=>setForm(p=>({...p,areaZip:e.target.value.replace(/[^0-9]/g,"").slice(0,5)}))} placeholder="e.g. 33901" maxLength={5}/>
              {form.areaZip&&form.areaZip.length===5&&<div style={{marginTop:8,padding:"8px 12px",background:`${GOLD}10`,borderRadius:6,border:`1px solid ${GOLD}33`,color:GOLD,fontSize:12,fontWeight:500}}>Default map center: {form.areaZip}</div>}
            </div>
          </>}

          {/* ── PIPELINE TAB ── */}
          {tab==="pipeline"&&<>
            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>🥐 Sales Pipeline Motto</div>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65,marginBottom:12}}>A motivational message shown under the page title across the entire app.</div>
              <label style={lbl}>Motto</label>
              <input style={inp} value={form.mottoText} onChange={e=>setForm(p=>({...p,mottoText:e.target.value}))} placeholder="Let's get that money! 💰" maxLength={80}/>
              {form.mottoText&&<div style={{marginTop:8,padding:"8px 12px",background:`${GOLD}10`,borderRadius:6,border:`1px solid ${GOLD}33`,color:GOLD,fontSize:12,fontStyle:"italic",fontWeight:500}}>Preview: "{form.mottoText}"</div>}
            </div>

            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>📦 Sample Packs</div>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65,marginBottom:14}}>Build reusable sample pack templates to track what was delivered to each lead during the Sample Sent stage.</div>
              <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14,padding:14,background:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",borderRadius:8,border:`1px solid ${T.borderSub}`}}>
                <div>
                  <label style={lbl}>Pack Name</label>
                  <input style={inp} value={newPack.name} onChange={e=>setNewPack(p=>({...p,name:e.target.value}))} placeholder="Standard Sample Box" onKeyDown={e=>{if(e.key==="Enter"&&newPack.name.trim())addPack();}}/>
                </div>
                <div>
                  <label style={lbl}>Items (one per line)</label>
                  <textarea style={{...inp,minHeight:80,resize:"vertical",lineHeight:1.6,fontSize:12}} value={newPack.items} onChange={e=>setNewPack(p=>({...p,items:e.target.value}))} placeholder={"Fresh croissants\nSourdough loaf\nBrioche buns"}/>
                </div>
                <button className="gbtn" style={{padding:"8px 18px",background:GOLD,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit",alignSelf:"flex-start",transition:"background .15s"}} onClick={addPack}>+ Add Pack</button>
              </div>
              {samplePacks.length===0
                ? <div style={{color:T.textDim,fontSize:12,textAlign:"center",padding:"6px 0"}}>No sample packs yet. Add one above.</div>
                : <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {samplePacks.map(pk=>(
                      <div key={pk.id} style={{padding:"12px 14px",borderRadius:8,background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",border:`1px solid ${T.borderSub}`,display:"flex",alignItems:"flex-start",gap:10}}>
                        <span style={{fontSize:16,flexShrink:0,marginTop:1}}>📦</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                            <span style={{color:T.text,fontWeight:700,fontSize:13}}>{pk.name}</span>
                            <span style={{background:`${GOLD}22`,color:GOLD,fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:20}}>{pk.items.length} item{pk.items.length!==1?"s":""}</span>
                          </div>
                          {pk.items.length>0&&<ul style={{paddingLeft:14,color:T.textSub,fontSize:11,lineHeight:1.7,margin:0}}>{pk.items.map((item,j)=><li key={j}>{item}</li>)}</ul>}
                        </div>
                        <button style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:18,padding:"0 4px",flexShrink:0,lineHeight:1}} title="Delete pack" onClick={()=>setSamplePacks(p=>p.filter(x=>x.id!==pk.id))}>×</button>
                      </div>
                    ))}
                  </div>
              }
            </div>
          </>}

          {/* ── EMAIL TAB ── */}
          {tab==="email"&&<>
            <div style={{padding:"12px 16px",borderRadius:8,background:configured?`${GREEN_OK}14`:`${AMBER}10`,border:`1px solid ${configured?GREEN_OK:AMBER}33`,display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:18}}>{configured?"✅":"💡"}</span>
              <div>
                <div style={{color:configured?GREEN_OK:AMBER,fontWeight:700,fontSize:13}}>{configured?"Email API Configured":"No Email API Configured"}</div>
                <div style={{color:T.textSub,fontSize:12,marginTop:2}}>{configured?"Direct sending enabled. Copy & Email App are always available too.":"Use Copy or Open in Email App to compose. Add an endpoint below to enable direct sending."}</div>
              </div>
            </div>

            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>Sender Identity</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div>
                  <label style={lbl}>From Name</label>
                  <input style={inp} value={form.fromName} onChange={e=>setForm(p=>({...p,fromName:e.target.value}))} placeholder="Elite Cravings Bakehouse"/>
                </div>
                <div>
                  <label style={lbl}>From Email</label>
                  <input style={inp} type="email" value={form.fromEmail} onChange={e=>setForm(p=>({...p,fromEmail:e.target.value}))} placeholder="sales@elitecravings.com"/>
                </div>
              </div>
            </div>

            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>SMTP / Email API Endpoint</div>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65,marginBottom:14}}>Provide a URL that accepts POST requests and sends email via your SMTP provider. Leave blank to use Copy &amp; Email App only.</div>
              <label style={lbl}>Endpoint URL</label>
              <input style={{...inp,marginBottom:14}} value={form.apiEndpoint} onChange={e=>setForm(p=>({...p,apiEndpoint:e.target.value}))} placeholder="https://your-server.com/api/send-email"/>
              <div style={{padding:"12px 14px",background:darkMode?"rgba(255,255,255,0.025)":"rgba(0,0,0,0.03)",borderRadius:6,border:`1px solid ${T.borderSub}`}}>
                <div style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>POST Payload (JSON)</div>
                <pre style={{color:T.textSub,fontSize:11,fontFamily:"'Courier New',monospace",lineHeight:1.7,margin:0,whiteSpace:"pre-wrap"}}>{payloadStr}</pre>
              </div>
            </div>

            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>Quick Setup Options</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[
                  ["⚡","Zapier / Make.com (No code)","Create a Webhook → Send Email action. No server required. Paste the webhook URL as your endpoint above."],
                  ["📨","EmailJS (Free tier)","Sign up at emailjs.com, connect Gmail or an SMTP service, then use a lightweight proxy to forward POST requests."],
                  ["🖥","Node.js + Nodemailer","Small Express server with Nodemailer. Supports Gmail, SendGrid, Outlook, or any SMTP host."],
                ].map(([ic,name,desc])=>(
                  <div key={name} style={{display:"flex",gap:12,padding:"11px 13px",background:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",borderRadius:7,border:`1px solid ${T.borderSub}`}}>
                    <span style={{fontSize:20,flexShrink:0}}>{ic}</span>
                    <div>
                      <div style={{color:T.text,fontWeight:600,fontSize:13,marginBottom:3}}>{name}</div>
                      <div style={{color:T.textSub,fontSize:12,lineHeight:1.5}}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{padding:"12px 16px",borderRadius:8,background:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",border:`1px solid ${T.borderSub}`,display:"flex",gap:10,alignItems:"flex-start"}}>
              <span style={{fontSize:16,flexShrink:0,marginTop:1}}>📋</span>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65}}>
                <strong style={{color:T.text}}>No setup required:</strong> In the Compose window, <strong style={{color:T.text}}>Copy</strong> copies the subject &amp; body to your clipboard, and <strong style={{color:T.text}}>Open in Email App</strong> pre-fills Gmail, Outlook, or your default mail client.
              </div>
            </div>
          </>}

          {/* ── CAMPAIGNS TAB ── */}
          {tab==="campaigns"&&<>
            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>📣 Campaign Email Providers</div>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65,marginBottom:16}}>API keys for bulk campaign sending via the Campaigns page. Each key is stored locally and never shared.</div>
              {[
                {key:"brevoKey",    label:"Brevo API Key",               color:"#0092FF", placeholder:"xkeysib-...",  hint:"Brevo Dashboard → SMTP & API → API Keys"},
                {key:"mandrillKey", label:"Mailchimp / Mandrill API Key", color:"#FFE01B", placeholder:"md-...",      hint:"Mailchimp → Transactional (Mandrill) → Settings → API Keys. Supports scheduled send."},
                {key:"ccApiKey",    label:"Constant Contact",            color:"#2563EB", placeholder:"API key or webhook URL…", hint:"Paste your CC webhook URL or API key. CC campaigns can also be opened directly in the CC dashboard."},
              ].map(({key,label,color,placeholder,hint},i,arr)=>(
                <div key={key} style={{marginBottom:i<arr.length-1?16:0,paddingBottom:i<arr.length-1?16:0,borderBottom:i<arr.length-1?`1px solid ${T.borderSub}`:"none"}}>
                  <label style={{...lbl,marginBottom:5,display:"flex",alignItems:"center",gap:7}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:color,flexShrink:0,display:"inline-block"}}/>
                    {label}
                  </label>
                  <input style={inp} type="password" value={form[key]} onChange={e=>setForm(p=>({...p,[key]:e.target.value}))} placeholder={placeholder}/>
                  <div style={{color:T.textDim,fontSize:10,marginTop:4,lineHeight:1.5}}>{hint}</div>
                  {form[key]&&<div style={{color:GREEN_OK,fontSize:11,marginTop:4,fontWeight:600}}>✓ Configured</div>}
                </div>
              ))}
              <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.borderSub}`,fontSize:11,color:T.textDim,lineHeight:1.6}}>
                <strong style={{color:T.textSub}}>Custom Webhook</strong> — configured in the Email tab → SMTP / Email API Endpoint. Forwards campaigns to any platform (Zapier, Make.com, n8n, SendGrid, etc.)
              </div>
            </div>
          </>}

          {/* ── AI & LOCATOR TAB ── */}
          {tab==="ai"&&<>
            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>✦ AI Settings</div>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65,marginBottom:14}}>Required for AI email generation and Lead Locator market analysis. Get your key at <span style={{color:GOLD}}>console.anthropic.com</span>. Stored locally — never sent anywhere except Anthropic's API.</div>
              <label style={lbl}>Claude API Key</label>
              <input style={inp} type="password" value={form.claudeKey} onChange={e=>setForm(p=>({...p,claudeKey:e.target.value}))} placeholder="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"/>
              {form.claudeKey&&<div style={{color:GREEN_OK,fontSize:11,marginTop:6,fontWeight:600}}>✓ Key configured</div>}
            </div>

            <div style={card}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>📍 Lead Locator Categories</div>
              <div style={{color:T.textSub,fontSize:12,lineHeight:1.65,marginBottom:14}}>Choose which business types appear in the Lead Locator search. Built-in categories are always active. Toggle additional categories below.</div>
              <div style={{marginBottom:14}}>
                <div style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Always Active</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                  {CATEGORIES.map(c=>(
                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 10px",borderRadius:20,background:`${c.color}18`,border:`1px solid ${c.color}44`}}>
                      <span style={{width:7,height:7,borderRadius:"50%",background:c.color,flexShrink:0}}/>
                      <span style={{color:c.color,fontSize:11,fontWeight:600}}>{c.icon} {c.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{paddingTop:14,borderTop:`1px solid ${T.borderSub}`}}>
                <div style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Additional Categories</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {EXTRA_CATS.map(ec=>{
                    const on=locatorExtras.includes(ec.id);
                    return(
                      <label key={ec.id} style={{display:"flex",alignItems:"center",gap:9,padding:"9px 12px",borderRadius:8,border:`1px solid ${on?ec.color+"66":T.borderSub}`,background:on?`${ec.color}10`:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",cursor:"pointer",transition:"all .15s"}}>
                        <input type="checkbox" checked={on} onChange={()=>toggleExtra(ec.id)} style={{accentColor:ec.color,width:14,height:14,flexShrink:0,cursor:"pointer"}}/>
                        <span style={{width:7,height:7,borderRadius:"50%",background:ec.color,flexShrink:0}}/>
                        <div style={{minWidth:0}}>
                          <div style={{color:on?ec.color:T.text,fontWeight:on?600:400,fontSize:12,lineHeight:1.2}}>{ec.icon} {ec.label}</div>
                          <div style={{color:T.textDim,fontSize:10,marginTop:2}}>Contact: {ec.contact}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {locatorExtras.length>0&&(
                  <div style={{marginTop:10,fontSize:11,color:GREEN_OK,fontWeight:600}}>✓ {locatorExtras.length} extra {locatorExtras.length===1?"category":"categories"} enabled</div>
                )}
              </div>
            </div>
          </>}

          {/* Save — visible for users with edit permission */}
          {can("editSettings")&&<div style={{display:"flex",justifyContent:"flex-end",paddingTop:8}}>
            <button className="gbtn" style={{padding:"11px 32px",background:`linear-gradient(135deg,${GOLD},${GOLD_HOV})`,border:"none",borderRadius:9,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",boxShadow:`0 2px 12px ${GOLD}33`}}
              onClick={()=>{onSave({...form,locatorExtras,samplePacks});notify("Settings saved! Reload to see logo changes.");}}>Save Settings</button>
          </div>}

          {/* ── USERS TAB (Admin only) ── */}
          {tab==="users"&&isAdmin&&<UsersPanel T={T} darkMode={darkMode} teamUsers={teamUsers} currentUid={userProfile?.uid} notify={notify}/>}

        </div>
      </div>
    </div>
  );
}

// ── Users Management Panel (Admin only) ──────────────────────────────────────
function UsersPanel({T,darkMode,teamUsers,currentUid,notify}){
  const [newUser,setNewUser]=useState({email:"",displayName:"",password:"",role:"manager"});
  const [newPerms,setNewPerms]=useState({...DEFAULT_MANAGER_PERMS});
  const [editUid,setEditUid]=useState(null);
  const [editRole,setEditRole]=useState("manager");
  const [editPerms,setEditPerms]=useState({...DEFAULT_MANAGER_PERMS});
  const [creating,setCreating]=useState(false);

  const card={background:T.card,borderRadius:14,padding:22,border:`1px solid ${T.border}`,boxShadow:T.cardShadow};
  const inp={width:"100%",padding:"10px 13px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:8,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",transition:"border-color .2s"};
  const lbl={color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:6};

  const startEdit=(u)=>{
    setEditUid(u.uid);setEditRole(u.role);setEditPerms(u.permissions||{...DEFAULT_MANAGER_PERMS});
  };

  const saveEdit=async()=>{
    await updateDoc(doc(db,'users',editUid),{role:editRole,permissions:editRole==='admin'?{...DEFAULT_MANAGER_PERMS,deleteLeads:true,manageCampaigns:true,editSettings:true,viewSettings:true}:editPerms});
    setEditUid(null);notify("User updated.");
  };

  const removeUser=async(uid)=>{
    await deleteDoc(doc(db,'users',uid));
    setEditUid(null);notify("User removed.");
  };

  const createUser=async()=>{
    if(!newUser.email||!newUser.password){notify("Email and password required.","error");return;}
    setCreating(true);
    try{
      const apiKey="AIzaSyAHwReyUwT0_mJ2b5WyxTy_iZvVvUQzHz0";
      let uid;
      // Try to create new Auth account
      const res=await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({email:newUser.email,password:newUser.password,returnSecureToken:false})
      });
      const data=await res.json();
      if(data.error){
        if(data.error.message==="EMAIL_EXISTS"){
          // Auth account exists — look up the UID and re-provision Firestore doc
          const lookupRes=await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`,{
            method:"POST",headers:{"Content-Type":"application/json"},
            body:JSON.stringify({email:newUser.email,password:newUser.password,returnSecureToken:false})
          });
          const lookupData=await lookupRes.json();
          if(lookupData.error){notify("Auth account exists but password doesn't match. Use the original password or reset it in Firebase Console.","error");setCreating(false);return;}
          uid=lookupData.localId;
        } else {
          notify(data.error.message,"error");setCreating(false);return;
        }
      } else {
        uid=data.localId;
      }
      const perms=newUser.role==='admin'?{...DEFAULT_MANAGER_PERMS,deleteLeads:true,manageCampaigns:true,editSettings:true,viewSettings:true}:newPerms;
      await setDoc(doc(db,'users',uid),{
        email:newUser.email,displayName:newUser.displayName||newUser.email.split("@")[0],
        role:newUser.role,permissions:perms,createdAt:new Date().toISOString()
      });
      setNewUser({email:"",displayName:"",password:"",role:"manager"});
      setNewPerms({...DEFAULT_MANAGER_PERMS});
      notify("User created!");
    }catch(e){notify("Failed to create user.","error");}
    setCreating(false);
  };

  const PermToggles=({perms,setPerms})=>(
    <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:10}}>
      {PERM_GROUPS.map(g=>(
        <div key={g.label} style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span style={{color:T.textSub,fontSize:11,fontWeight:600,width:75,flexShrink:0}}>{g.label}</span>
          {g.perms.map(p=>(
            <label key={p.id} style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:11,color:perms[p.id]?GREEN_OK:T.textDim}}>
              <input type="checkbox" checked={!!perms[p.id]} onChange={()=>setPerms(prev=>({...prev,[p.id]:!prev[p.id]}))} style={{accentColor:GOLD,width:13,height:13,cursor:"pointer"}}/>
              {p.l}
            </label>
          ))}
        </div>
      ))}
    </div>
  );

  return(<>
    {/* Team members list */}
    <div style={card}>
      <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>👥 Team Members</div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {teamUsers.map(u=>{
          const isMe=u.uid===currentUid;
          const editing=editUid===u.uid;
          return(
            <div key={u.uid} style={{padding:"12px 14px",borderRadius:8,background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",border:`1px solid ${editing?GOLD+"55":T.borderSub}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:32,height:32,borderRadius:8,background:`linear-gradient(135deg,${u.role==='admin'?GOLD:AMBER},${u.role==='admin'?GOLD:AMBER}cc)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#000",fontWeight:800,fontSize:12,flexShrink:0}}>
                  {(u.displayName||u.email||"?")[0].toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:T.text,fontWeight:600,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {u.displayName||u.email.split("@")[0]}{isMe&&<span style={{color:T.textDim,fontWeight:400}}> (You)</span>}
                  </div>
                  <div style={{color:T.textDim,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email}</div>
                </div>
                <span style={{fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20,background:u.role==='admin'?`${GOLD}22`:`${AMBER}22`,color:u.role==='admin'?GOLD:AMBER,textTransform:"uppercase",letterSpacing:"0.06em"}}>{u.role}</span>
                {!isMe&&<button className="abtn" onClick={()=>editing?setEditUid(null):startEdit(u)}
                  style={{padding:"4px 10px",background:"none",border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.textSub,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>
                  {editing?"Cancel":"Edit"}
                </button>}
              </div>
              {editing&&<div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.borderSub}`}}>
                <div style={{marginBottom:10}}>
                  <label style={lbl}>Role</label>
                  <select style={{...inp,width:160}} value={editRole} onChange={e=>setEditRole(e.target.value)}>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                {editRole==="manager"&&<PermToggles perms={editPerms} setPerms={setEditPerms}/>}
                <div style={{display:"flex",gap:8,marginTop:14}}>
                  <button className="gbtn" onClick={saveEdit}
                    style={{padding:"7px 18px",background:GOLD,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Save</button>
                  <button onClick={()=>removeUser(u.uid)}
                    style={{padding:"7px 14px",background:"none",border:`1px solid ${RED}44`,borderRadius:6,color:RED,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove User</button>
                </div>
              </div>}
            </div>
          );
        })}
      </div>
    </div>

    {/* Add new user */}
    <div style={card}>
      <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>➕ Add New User</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div>
          <label style={lbl}>Email *</label>
          <input style={inp} type="email" value={newUser.email} onChange={e=>setNewUser(p=>({...p,email:e.target.value}))} placeholder="user@company.com"/>
        </div>
        <div>
          <label style={lbl}>Display Name</label>
          <input style={inp} value={newUser.displayName} onChange={e=>setNewUser(p=>({...p,displayName:e.target.value}))} placeholder="John Doe"/>
        </div>
        <div>
          <label style={lbl}>Password *</label>
          <input style={inp} type="password" value={newUser.password} onChange={e=>setNewUser(p=>({...p,password:e.target.value}))} placeholder="Min 6 characters"/>
        </div>
        <div>
          <label style={lbl}>Role</label>
          <select style={inp} value={newUser.role} onChange={e=>setNewUser(p=>({...p,role:e.target.value}))}>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
      {newUser.role==="manager"&&<>
        <div style={{color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginTop:16,marginBottom:4}}>Permissions</div>
        <PermToggles perms={newPerms} setPerms={setNewPerms}/>
      </>}
      <div style={{marginTop:16}}>
        <button className="gbtn" onClick={createUser} disabled={creating}
          style={{padding:"10px 24px",background:GOLD,border:"none",borderRadius:8,color:"#000",fontWeight:700,fontSize:13,cursor:creating?"wait":"pointer",fontFamily:"inherit",opacity:creating?0.7:1}}>
          {creating?"Creating...":"Create User"}
        </button>
      </div>
    </div>
  </>);
}

// ── Campaign Provider Send Functions ──────────────────────────────────────────
async function sendViaBrevo(recipients, subject, body, settings) {
  if(!settings?.brevoKey) throw new Error("Brevo API key not configured. Go to Settings → Campaign Email Providers.");
  const html=`<div style="font-family:Arial,sans-serif;max-width:600px;line-height:1.75">${body.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br/>")}</div>`;
  const res=await fetch("https://api.brevo.com/v3/smtp/email",{
    method:"POST",
    headers:{"Content-Type":"application/json","api-key":settings.brevoKey},
    body:JSON.stringify({
      sender:{name:settings.fromName||"Elite Cravings Bakehouse",email:settings.fromEmail||""},
      to:recipients.map(r=>({email:r.email,name:r.contact||r.company})),
      subject,textContent:body,htmlContent:html,
    })
  });
  if(!res.ok){const d=await res.json().catch(()=>({}));throw new Error(d.message||`Brevo error (${res.status})`);}
}

async function sendViaMandrill(recipients, subject, body, settings, sendAt) {
  if(!settings?.mandrillKey) throw new Error("Mandrill API key not configured. Go to Settings → Campaign Email Providers.");
  const html=`<div style="font-family:Arial,sans-serif;max-width:600px;line-height:1.75">${body.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br/>")}</div>`;
  const payload={
    key:settings.mandrillKey,
    message:{
      from_email:settings.fromEmail||"",from_name:settings.fromName||"Elite Cravings Bakehouse",
      to:recipients.map(r=>({email:r.email,name:r.contact||r.company,type:"to"})),
      subject,text:body,html,
    },
  };
  if(sendAt) payload.send_at=sendAt;
  const res=await fetch("https://mandrillapp.com/api/1.0/messages/send.json",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
  if(!res.ok) throw new Error(`Mandrill error (${res.status})`);
}

async function sendViaWebhookCampaign(recipients, subject, body, settings, scheduledAt) {
  if(!settings?.apiEndpoint) throw new Error("Custom webhook endpoint not configured. Go to Settings → SMTP / Email API.");
  const res=await fetch(settings.apiEndpoint,{
    method:"POST",headers:{"Content-Type":"application/json"},
    body:JSON.stringify({
      campaign:true,
      recipients:recipients.map(r=>({email:r.email,name:r.contact||r.company,company:r.company})),
      subject,body,fromName:settings.fromName||"Elite Cravings Bakehouse",fromEmail:settings.fromEmail||"",
      scheduledAt:scheduledAt||null,
    })
  });
  if(!res.ok) throw new Error(`Webhook error (${res.status})`);
}

// ── Campaigns ─────────────────────────────────────────────────────────────────
const PROVIDERS=[
  {id:"brevo",    label:"Brevo",              color:"#0092FF",settingKey:"brevoKey",    note:"Direct API — send now. Scheduled campaigns store in Firebase."},
  {id:"mandrill", label:"Mailchimp (Mandrill)",color:"#EFCC31",settingKey:"mandrillKey", note:"Transactional via Mandrill. Supports native scheduled send."},
  {id:"cc",       label:"Constant Contact",   color:"#2563EB",settingKey:"ccApiKey",    note:"Copies content to clipboard and opens CC dashboard."},
  {id:"webhook",  label:"Custom Webhook",     color:"#6B7280",settingKey:"apiEndpoint", note:"Posts campaign payload to your configured webhook endpoint."},
];

function CampaignsView({T,darkMode,isMobile,leads,emailSettings,notify}){
  const [campaigns,setCampaigns]=useState([]);
  const [creating,setCreating]=useState(false);

  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"campaigns"),snap=>{
      setCampaigns(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>b.createdAt>a.createdAt?1:-1));
    }, err=>console.warn("Firestore campaigns listener error:",err.message));
    return()=>unsub();
  },[]);

  // Auto-send due scheduled campaigns on load
  useEffect(()=>{
    const due=campaigns.filter(c=>c.status==="scheduled"&&c.scheduledAt&&new Date(c.scheduledAt)<=new Date());
    due.forEach(async c=>{
      try{
        const recips=c.recipients||[];
        if(c.provider==="brevo")      await sendViaBrevo(recips,c.subject,c.body,emailSettings);
        else if(c.provider==="mandrill") await sendViaMandrill(recips,c.subject,c.body,emailSettings);
        else if(c.provider==="webhook")  await sendViaWebhookCampaign(recips,c.subject,c.body,emailSettings);
        await updateDoc(doc(db,"campaigns",c.id),{status:"sent",sentAt:new Date().toISOString()});
        notify(`Campaign "${c.name}" auto-sent!`);
      }catch(e){
        await updateDoc(doc(db,"campaigns",c.id),{status:"failed",failNote:e.message});
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[campaigns.length]);

  const deleteCampaign=async id=>{await deleteDoc(doc(db,"campaigns",id));notify("Campaign deleted.");};

  const statusColor=s=>s==="sent"?GREEN_OK:s==="scheduled"?GOLD:s==="failed"?RED:T.textDim;
  const provInfo=id=>PROVIDERS.find(p=>p.id===id)||PROVIDERS[3];

  if(creating) return <CampaignCreator T={T} darkMode={darkMode} isMobile={isMobile} leads={leads} emailSettings={emailSettings} notify={notify} onBack={()=>setCreating(false)}/>;

  return(
    <div style={{flex:1,overflow:"auto",padding:"20px 24px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
        <div>
          <h2 style={{color:T.text,fontSize:20,fontWeight:800,letterSpacing:"-0.03em",marginBottom:6}}>Email Campaigns</h2>
          <p style={{color:T.textSub,fontSize:13,lineHeight:1.5}}>Create and schedule bulk campaigns via Brevo, Mailchimp, Constant Contact, or your webhook.</p>
        </div>
        <button className="gbtn" onClick={()=>setCreating(true)}
          style={{padding:"10px 20px",background:`linear-gradient(135deg,${GOLD},${GOLD_HOV})`,border:"none",borderRadius:9,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",flexShrink:0,boxShadow:`0 2px 12px ${GOLD}33`}}>
          + New Campaign
        </button>
      </div>

      {!campaigns.length&&(
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",paddingTop:60,gap:12}}>
          <div style={{fontSize:44}}>📣</div>
          <div style={{fontSize:15,color:T.textSub,fontWeight:600}}>No campaigns yet</div>
          <div style={{fontSize:13,color:T.textDim,textAlign:"center",maxWidth:360,lineHeight:1.65}}>Create your first campaign to send scheduled emails to your pipeline leads via your preferred email platform.</div>
          <button className="gbtn" onClick={()=>setCreating(true)}
            style={{marginTop:6,padding:"9px 22px",background:GOLD,border:"none",borderRadius:8,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",transition:"background .2s"}}>
            + New Campaign
          </button>
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {campaigns.map(c=>{
          const pv=provInfo(c.provider);
          return(
            <div key={c.id} style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,padding:"18px 22px",display:"flex",alignItems:"center",gap:16,boxShadow:T.cardShadow,transition:"transform .2s ease,box-shadow .2s ease"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                  <span style={{color:T.text,fontWeight:700,fontSize:14}}>{c.name}</span>
                  <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,background:pv.color+"28",color:pv.color,letterSpacing:"0.04em"}}>{pv.label}</span>
                  <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,background:statusColor(c.status)+"22",color:statusColor(c.status),letterSpacing:"0.04em",textTransform:"capitalize"}}>{c.status}</span>
                </div>
                <div style={{color:T.textSub,fontSize:12,marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:500}}>{c.subject||"(no subject)"}</div>
                <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                  <span style={{color:T.textDim,fontSize:11}}>{c.recipientCount||0} recipient{c.recipientCount!==1?"s":""}</span>
                  {c.sentAt&&<span style={{color:T.textDim,fontSize:11}}>Sent {new Date(c.sentAt).toLocaleString()}</span>}
                  {c.scheduledAt&&c.status==="scheduled"&&<span style={{color:GOLD,fontSize:11}}>⏱ {new Date(c.scheduledAt).toLocaleString()}</span>}
                  {c.failNote&&<span style={{color:RED,fontSize:11}}>⚠ {c.failNote}</span>}
                  <span style={{color:T.textDim,fontSize:11}}>{new Date(c.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <button onClick={()=>deleteCampaign(c.id)}
                style={{background:"none",border:`1px solid ${RED}33`,borderRadius:6,color:RED,cursor:"pointer",padding:"5px 10px",fontSize:11,fontFamily:"inherit",flexShrink:0,transition:"all .15s"}}>
                Delete
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Campaign Creator ───────────────────────────────────────────────────────────
function CampaignCreator({T,darkMode,isMobile,leads,emailSettings,notify,onBack}){
  const [form,setForm]=useState({name:"",subject:"",body:"",provider:"brevo",sendNow:true,scheduledAt:""});
  const [selLeads,setSelLeads]=useState([]);
  const [catF,setCatF]=useState("all");
  const [prompt,setPrompt]=useState("");
  const [gen,setGen]=useState(false);
  const [sending,setSending]=useState(false);
  const [err,setErr]=useState("");
  const setF=(k,v)=>setForm(p=>({...p,[k]:v}));

  const validLeads=leads.filter(l=>l.email&&(catF==="all"||l.cat===catF));
  const selectedData=leads.filter(l=>selLeads.includes(l.id)&&l.email);
  const toggleLead=id=>setSelLeads(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const selectAll=()=>setSelLeads(validLeads.map(l=>l.id));
  const selectNone=()=>setSelLeads([]);

  const generate=async()=>{
    if(!prompt.trim()){setErr("Enter a prompt first.");return;}
    if(!emailSettings?.claudeKey){setErr("Claude API key required in Settings → ✦ AI Settings.");return;}
    setGen(true);setErr("");
    try{
      const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",
        headers:{"Content-Type":"application/json","x-api-key":emailSettings.claudeKey,"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:700,
          system:"You are a professional sales copywriter for Elite Cravings Bakehouse, a commercial artisan bakery in Estero, FL. Write a concise, warm email campaign under 180 words with one clear call to action. Return ONLY the email body.",
          messages:[{role:"user",content:`Campaign: ${form.name||"bakery outreach"}\nInstruction: ${prompt}`}]})});
      const data=await res.json();
      const text=data.content?.map(b=>b.text||"").join("")||"";
      if(text)setF("body",text.trim());else setErr("No response — try rephrasing.");
    }catch{setErr("Generation failed.");}
    setGen(false);
  };

  const saveCampaign=async(status)=>{
    if(!form.name.trim()){setErr("Campaign name is required.");return;}
    if(status!=="draft"&&!form.subject.trim()){setErr("Subject line is required.");return;}
    if(status!=="draft"&&!form.body.trim()){setErr("Email body is required.");return;}
    if(status!=="draft"&&!selLeads.length){setErr("Select at least one recipient.");return;}
    if(status!=="draft"&&!form.sendNow&&!form.scheduledAt){setErr("Pick a scheduled date/time.");return;}
    setSending(true);setErr("");
    try{
      // CC: copy to clipboard + open dashboard
      if(status!=="draft"&&form.provider==="cc"){
        navigator.clipboard.writeText(`Subject: ${form.subject}\n\n${form.body}`).catch(()=>{});
        window.open("https://login.constantcontact.com","_blank");
        notify("Content copied — paste into Constant Contact!");
      } else if(status==="sent"||status==="scheduled"){
        const sendAtStr=!form.sendNow&&form.scheduledAt?new Date(form.scheduledAt).toISOString().replace("T"," ").slice(0,19):null;
        if(form.sendNow){
          if(form.provider==="brevo")    await sendViaBrevo(selectedData,form.subject,form.body,emailSettings);
          else if(form.provider==="mandrill") await sendViaMandrill(selectedData,form.subject,form.body,emailSettings);
          else if(form.provider==="webhook")  await sendViaWebhookCampaign(selectedData,form.subject,form.body,emailSettings);
        } else if(form.provider==="mandrill"){
          await sendViaMandrill(selectedData,form.subject,form.body,emailSettings,sendAtStr);
        }
        // Brevo scheduled → store in Firebase; auto-sent on next load
        // Webhook scheduled → pass scheduledAt in payload
        else if(form.provider==="webhook"){
          await sendViaWebhookCampaign(selectedData,form.subject,form.body,emailSettings,sendAtStr);
        }
      }
      await addDoc(collection(db,"campaigns"),{
        name:form.name,subject:form.subject,body:form.body,provider:form.provider,
        recipientCount:selLeads.length,
        recipients:selectedData.map(r=>({id:r.id,company:r.company,email:r.email,contact:r.contact})),
        status:status==="draft"?"draft":form.sendNow?"sent":"scheduled",
        scheduledAt:!form.sendNow?form.scheduledAt:null,
        createdAt:new Date().toISOString(),
        sentAt:status!=="draft"&&form.sendNow?new Date().toISOString():null,
      });
      notify(status==="draft"?"Draft saved!":form.sendNow?`Campaign sent to ${selLeads.length} leads!`:"Campaign scheduled!");
      onBack();
    }catch(e){setErr(e.message||"Send failed.");}
    setSending(false);
  };

  const inp={width:"100%",padding:"10px 13px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:8,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",transition:"border-color .2s"};
  const card={background:T.card,borderRadius:14,padding:22,border:`1px solid ${T.border}`,marginBottom:14,boxShadow:T.cardShadow};
  const lbl={color:T.textDim,fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:5};
  const pv=PROVIDERS.find(p=>p.id===form.provider)||PROVIDERS[0];
  const hasKey=!!(emailSettings?.[pv.settingKey]);

  return(
    <div style={{flex:1,overflow:"auto",padding:"18px 22px"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:22}}>
        <button style={{background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:12,fontFamily:"inherit",padding:0}} onClick={onBack}>← Back</button>
        <h2 style={{color:T.text,fontSize:18,fontWeight:700,letterSpacing:"-0.02em"}}>New Campaign</h2>
      </div>

      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16,alignItems:"start",maxWidth:900}}>

        {/* LEFT — Setup + Recipients */}
        <div>
          <div style={card}>
            <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>Campaign Details</div>
            <div style={{marginBottom:12}}>
              <label style={lbl}>Campaign Name</label>
              <input style={inp} value={form.name} onChange={e=>setF("name",e.target.value)} placeholder="e.g. March Restaurant Outreach"/>
            </div>
            <div style={{marginBottom:12}}>
              <label style={lbl}>Email Provider</label>
              <select style={inp} value={form.provider} onChange={e=>setF("provider",e.target.value)}>
                {PROVIDERS.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <div style={{marginTop:7,padding:"7px 11px",borderRadius:7,background:pv.color+"18",border:`1px solid ${pv.color}33`,display:"flex",alignItems:"center",gap:8}}>
                <span style={{width:7,height:7,borderRadius:"50%",background:pv.color,flexShrink:0}}/>
                <span style={{fontSize:11,color:T.textSub,flex:1}}>{pv.note}</span>
                {!hasKey&&<span style={{fontSize:10,color:AMBER,fontWeight:600,flexShrink:0}}>⚠ Key not set</span>}
                {hasKey&&<span style={{fontSize:10,color:GREEN_OK,fontWeight:600,flexShrink:0}}>✓ Ready</span>}
              </div>
            </div>
            <div>
              <label style={lbl}>Send Timing</label>
              <div style={{display:"flex",gap:8,marginBottom:form.sendNow?0:10}}>
                {[["Send Now",true],["Schedule",false]].map(([label,val])=>(
                  <label key={label} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",flex:1,padding:"8px 12px",borderRadius:7,border:`1px solid ${form.sendNow===val?GOLD:T.borderSub}`,background:form.sendNow===val?`${GOLD}12`:"none",transition:"all .15s"}}>
                    <input type="radio" name="timing" checked={form.sendNow===val} onChange={()=>setF("sendNow",val)} style={{accentColor:GOLD}}/>
                    <span style={{color:form.sendNow===val?GOLD:T.textSub,fontSize:12,fontWeight:form.sendNow===val?600:400}}>{label}</span>
                  </label>
                ))}
              </div>
              {!form.sendNow&&(
                <div style={{marginTop:8}}>
                  <input type="datetime-local" style={inp} value={form.scheduledAt} onChange={e=>setF("scheduledAt",e.target.value)}/>
                  {form.provider!=="mandrill"&&<div style={{color:T.textDim,fontSize:10,marginTop:4,lineHeight:1.5}}>Stored in Firebase. Auto-sends when app is open at scheduled time. Mandrill supports server-side scheduling.</div>}
                </div>
              )}
            </div>
          </div>

          {/* Recipients */}
          <div style={card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase"}}>Recipients</div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <select style={{padding:"3px 8px",background:T.input,border:`1px solid ${T.borderSub}`,borderRadius:6,color:T.textSub,fontSize:11,fontFamily:"inherit",outline:"none",cursor:"pointer"}}
                  value={catF} onChange={e=>{setCatF(e.target.value);selectNone();}}>
                  <option value="all">All Categories</option>
                  {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
                <button style={{background:"none",border:"none",color:GOLD,cursor:"pointer",fontSize:11,fontFamily:"inherit"}} onClick={selectAll}>All</button>
                <button style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:11,fontFamily:"inherit"}} onClick={selectNone}>None</button>
              </div>
            </div>
            <div style={{maxHeight:260,overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>
              {!validLeads.length&&<div style={{color:T.textDim,fontSize:12,textAlign:"center",padding:"20px 0"}}>No leads with email addresses match this filter.</div>}
              {validLeads.map(l=>{
                const cat=getCat(l.cat),checked=selLeads.includes(l.id);
                return(
                  <label key={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",borderRadius:7,cursor:"pointer",background:checked?`${GOLD}10`:"none",border:`1px solid ${checked?GOLD+"44":T.borderSub}`,transition:"all .12s"}}>
                    <input type="checkbox" checked={checked} onChange={()=>toggleLead(l.id)} style={{accentColor:GOLD,width:14,height:14,flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{color:T.text,fontSize:12,fontWeight:checked?600:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.company}</div>
                      <div style={{color:T.textDim,fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.email}</div>
                    </div>
                    <span style={{fontSize:9,fontWeight:600,padding:"1px 6px",borderRadius:20,background:cat.color+"33",color:cat.color,flexShrink:0}}>{cat.icon}</span>
                  </label>
                );
              })}
            </div>
            <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.borderSub}`,fontSize:12,color:T.textSub}}>
              <span style={{color:selLeads.length?GOLD:T.textDim,fontWeight:700}}>{selLeads.length}</span>
              <span style={{marginLeft:5}}>of {validLeads.length} selected</span>
            </div>
          </div>
        </div>

        {/* RIGHT — Email Content */}
        <div>
          <div style={card}>
            <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:12}}>Email Content</div>

            {/* AI strip */}
            <div style={{marginBottom:14,padding:"12px 14px",background:darkMode?"rgba(201,168,76,0.04)":"rgba(201,168,76,0.06)",borderRadius:8,border:`1px solid ${T.border}`}}>
              <div style={{color:GOLD,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>✦ AI Generator</div>
              <div style={{display:"flex",gap:8}}>
                <input style={{...inp,flex:1}} placeholder='e.g. "Spring menu promo for restaurants"'
                  value={prompt} onChange={e=>setPrompt(e.target.value)} onKeyDown={e=>e.key==="Enter"&&generate()}/>
                <button className="gbtn" disabled={gen} onClick={generate}
                  style={{padding:"9px 14px",background:GOLD,border:"none",borderRadius:7,color:"#000",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:5,flexShrink:0,transition:"background .15s"}}>
                  {gen?<Spnr/>:"Generate ✦"}
                </button>
              </div>
            </div>

            <div style={{marginBottom:12}}>
              <label style={lbl}>Subject Line</label>
              <input style={inp} value={form.subject} onChange={e=>setF("subject",e.target.value)} placeholder="Fresh-Baked Artisan Breads — Elite Cravings Bakehouse"/>
            </div>
            <div>
              <label style={lbl}>Email Body</label>
              <textarea style={{...inp,minHeight:300,resize:"vertical",lineHeight:1.75,padding:"11px 12px"}} value={form.body} onChange={e=>setF("body",e.target.value)}/>
            </div>
          </div>

          {err&&<div style={{color:RED,fontSize:12,padding:"10px 14px",borderRadius:8,background:`${RED}12`,border:`1px solid ${RED}33`,marginBottom:14}}>{err}</div>}

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={{padding:"10px 18px",background:"none",border:`1px solid ${T.border}`,borderRadius:8,color:T.textSub,cursor:"pointer",fontFamily:"inherit",fontSize:13,transition:"all .15s"}}
              onClick={()=>saveCampaign("draft")}>Save Draft</button>
            <button className="gbtn" disabled={sending}
              style={{padding:"10px 22px",background:GOLD,border:"none",borderRadius:8,color:"#000",fontWeight:700,fontSize:13,cursor:sending?"not-allowed":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:8,transition:"background .15s"}}
              onClick={()=>saveCampaign(form.sendNow?"sent":"scheduled")}>
              {sending
                ?<><Spnr/>&nbsp;{form.sendNow?"Sending…":"Scheduling…"}</>
                :form.provider==="cc"
                  ?`📋 Copy & Open CC`
                  :form.sendNow
                    ?`📣 Send to ${selLeads.length||"?"} leads`
                    :"⏱ Schedule Campaign"
              }
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
